import { action, computed, makeObservable, observable } from 'mobx';
import { message } from 'antd';
import {
  browserPushUrl,
  cache,
  CountryCode,
  debounce,
  fetchQueryParams,
  formatPrice,
  getCurrency,
  getEleOffset,
  getSignal,
  handleQueryParams,
  isWechat,
  querySelector,
  scrollToSmooth,
  LANG,
  strTrim,
  isEmpty,
  uniq,
  isBroswer,
  isSameStr,
  toggleGlobalScroll,
  historyReplaceState,
  PromiseAllSettled,
  getUserAccount,
  isAvailableForSale,
} from '@/utils';
import { SERVER } from '@/request/config';
import { httpRequest, httpRequestLimitNums } from '@/request';
import { handleDisplays } from '@/utils/configs/productDisplays.config';
import { encodeURIEncryptFE, encryptBE } from '@/utils/libs/JSEncrypt';
import ProfessionalDiscount from '@/pages/product/components/ProductDiscount/components/ProfessionalDiscountEntry/config';
import validator from '@/utils/validator';
import ThirdPartyPlatformConfig from '@/utils/configs/ThirdPartyPlatform.config';
import { enableStaticRendering } from 'mobx-react';
import { ThirdPartyTracker, OrderTracker, EcommerceTracker, PaymentTracker, CartTracker } from '@/utils/Tracker';
import {
  bindProvinceCode,
  getDeliveryObj,
  handleItemPrice,
  preCheckForterToken,
  calcOrderConfirmPrice,
} from '@/utils/mobxUtils/order';
import { feishuRobotSend } from '@/utils/feishuRobot';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import ApplePay from '@/utils/payment/ApplePay.config';
import GooglePay from '@/utils/payment/GooglePay.config';
import { ORDER_STATE } from '@/utils/configs/state.config';
import { cloneDeep, sortBy } from 'lodash';
import { forterResult } from '@/utils/configs/payment.config';
import CustomSkinHelper from '@/pages/product/components/CustomSkin/helper';
import { isNeedHidePriceProduct, isPreSaleCommodity } from '@/utils/configs/product.config';
import { diffCloudBenifitCountry, hasCloudService } from '@/components/ExtraBindService/CloudServices.config';
import StoreBase from './Base';
import { openSearchOrderReport } from '@/pages/search/report';
import { OrderToPayAdaptiveEnum } from './types/payment';
import { ExtraServiceType } from '@/types/extraService';
import { AddToCartType } from '@/utils/product';

// SSR使用：静态渲染模式下，MobX不会跟踪依赖关系，不会触发重新渲染
enableStaticRendering(typeof window === 'undefined');

const scrollToEle = async (ele) => {
  const e = await querySelector(ele);
  const objOffset = getEleOffset(e);
  const headerHeight = document.querySelector('#g-header').offsetHeight;
  scrollToSmooth(objOffset.y - headerHeight - 20);
};

// 购物车页 - 购物车本地缓存
const restoreCartItems = () => {
  let raw = cache.get('cart_items');
  return raw ? JSON.parse(raw) : [];
};

// 结账页 - 购物车本地缓存
const restoreOrderCartItems = () => {
  let raw = cache.get('order_cart_items');
  return raw ? JSON.parse(raw) : [];
};

// 云服务升级结账页 - 本地缓存
const restoreCloudOrderCartItems = () => {
  let raw = cache.get('cloud_upgrade_items');
  return raw ? JSON.parse(raw) : [];
};

const cart_items = restoreCartItems();
const order_cart_items = restoreOrderCartItems();
const cloud_order_cart_items = restoreCloudOrderCartItems();

/** 价格跳动问题的技术评审文档：看图里的方案二，方案一已弃用 https://arashivision.feishu.cn/wiki/CnoRwzapoiZjPHkmqnEcC84zn1d */
// 用来阻塞计算购物车总价，等待网络请求获取Tax和ShippingFee后再计算
let isCalculating = false;
// 缓存上一次的购物车总价
let previousCartTotalPrice = null;

// 增值服务由后端进行过滤
const FilterCartCids = [...CustomSkinHelper.CustomSkinCidList];

const updateCartToRemote = debounce((newItems) => {
  if (cache.getCookie('insta_access_token') && newItems) {
    newItems = newItems
      .map((item) => {
        return {
          id: item.commodityId,
          number: item.count,
        };
      })
      .filter((o) => o.number > 0)
      .filter((o) => !FilterCartCids.includes(o.id))
      .filter((o) => !isPreSaleCommodity({ cid: o.id }));

    const url = SERVER.API_SERVER_TRADE + '/cart/updateCart';

    httpRequest({
      url,
      method: 'post',
      instaToken: true,
      data: {
        items: newItems,
      },
    });
  }
}, 250);

class Order extends StoreBase {
  constructor(props) {
    super(props);
    makeObservable(this);
    if (isBroswer) {
      this.resetAfterOrderCreate();
    }
  }

  /**
   * @description 含危险品订单的场景，一共有6种
   * 根据用户所选的套餐判断危险品订单的场景——包括购物车页/结算页
   * 对于危险品/普通套餐/含电套餐/充电座的定义在 https://arashivision.feishu.cn/wiki/NV8JwDrQZiwT9CkYLbqcnvG1nGb
   * 危险品订单在不同国家的限制级别不同，根据不同国家的不同场景分别回返回6种code
   * ———无需限制国家返回的code为「4」
   * ———限制危险物流的国家——
   * 1: 「危险品 + 普通套餐」
   * 2：「含有titan电池+含电套餐」或「仅包含titan电池+充电座」
   * 3：「仅包含危险品」或「仅包含危险品+充电座」
   * 4: 「不含危险品」 或 「包含危险品+含电套餐」
   * ——禁止危险物流的国家——
   * 5: 「危险品 + 普通套餐」
   * 6: 「含有titan电池+含电套餐」
   */
  @observable orderSceneType = 4;

  @observable cartStickyBarVisible = false;

  @observable cartStickyBarElement = null;

  @observable order_wechat_qr_payment = '';

  @observable order_wechat_qr_payment_number = '';

  @observable detailOrder = {};

  @observable simpleDetailOrder = {}; // 用于pay/transfer页面的数据

  @observable cartExtraFee = {};

  @observable locationsArea = null;

  @observable cart_items = cart_items;

  @observable order_cart_items = this.root.app.isCloudUpgradeOrderConfirmPage
    ? cloud_order_cart_items
    : order_cart_items;

  @observable cart_recommend = [];

  @observable gifts = [];

  @observable orderConfirmGifts = [];

  @observable globalServiceTipsModalVisible = false;

  // 用户优惠券列表
  @observable userGiftCards = [];

  // 收税州
  @observable taxesStateData = [];

  // 用户收货地址列表
  @observable _deliveries = [];

  // 当前用户正在操作的收货地址
  @observable _deliveryDetail = {};

  // 移动端收货地址编辑状态
  @observable _deliveryActionCallback = null;

  // 账单地址与收货地址保持一致
  @observable _billingIsDelivery = true;

  // 账单地址
  @observable _billingDetail = {};

  @observable createOrderLoading = false; // 创建订单loading

  @observable _beingPaid = false; // 支付loading

  // confirm移动端表单编辑状态，可用于控制页面样式
  @observable confirmFormEditing = {
    _deliveryEditing: false, // 收货地址表单编辑中
    _deliveryValidating: false, // 地址正在校验中
    _saleTaxFetchLoading: false, // 税率校验中
    _importDutiesFetchLoading: false, // 美国进口关税计算中
    _billingEditing: false, // 移动端账单地址编辑状态
    _invoiceEditing: false, // 发票信息编辑状态
    _creditCardEditing: false, // 信用卡消息填写中
    _taxAddressNeedEdit: false, // 选择错误的快递信息地址点击下一步将阻断
  };

  // 结账页金额小计合计
  @observable _orderCount = {
    tax: 0,
    salesTax: 0, // 消费税
    total: 0,
    ship: 0,
    items: 0,
    signal: getSignal(this.root.area.country),
    currency: getCurrency(this.root.area.country),
    isFreeOrder: false,
    isTradeupOrder: false,
    isProfessionalDiscountOrder: false,
    is8kLiveOrder: false,
    isCareServiceOrder: false, // 是否包含care
    isCarePlusServiceOrder: false, // 是否包含carePlus
    isWarrantyServiceOrder: false, // 是否包含延保服务
    isOnlyBindServiceOrder: false, // 是否是单独购买虚拟服务的订单
    coupon: '', // 优惠码
    couponType: '', // 优惠码类型 coupon_code || gift_card_code || reseller_code
    couponAmount: 0,
    giftCardAmount: 0,
    resellerCouponAmount: 0,
    tradeUpCouponAmount: 0,
    professionalCouponAmount: 0,
    paypalDiscount: 0,
    couponDiscountsInfo: [], // coupon打折的具体套餐信息，用于刷新场景下的金额计算
  };

  /* 条款默认勾选 */
  @observable _orderAgreementCheckState = {
    insta360SalePolicy: true,
    insta360PrivacyPolicy: true,
    live8k: true,
    tradeup: true,
    careService: true,
    cloudService: true,
    warrantyService: true,
    carePlusService: true,
    insta360Subscribe: false,
  };

  @observable _payment = {};

  // 结账页创建订单信用卡支付回调
  @observable _orderInvoice = {
    type: 'none', // none || full_electronic_platform_invoice || full_electronic_dedicated_invoice
    done: true,
    info: {},
  };

  @observable
  form = {
    billing: null,
    delivery: null,
    contact: null,
    subscription: {},
  };

  @observable fetchingProvince = false;

  @observable ProvinceOceanCodes = {};

  // 国际区号
  @observable PhoneCodes = [];

  @observable taxModalForMXType = -1;

  // -1：订单正常不显示，1：包含专业相机，2：需要拆单
  @computed
  get TaxModalForMXVisible() {
    return this.taxModalForMXType > 0;
  }

  // 移动端结账页步骤进度
  @observable currentConfirmStep = 0;

  // 移动端结账页跳转步骤
  @action
  changeConfirmStep = (step) => {
    this.currentConfirmStep = step;
    scrollToSmooth(0, 0);
  };

  @action
  setStateValue = async (key, value, cb) => {
    if (key === '_billingIsDelivery') {
      this._billingDetail = value ? this._deliveryDetail : {};
    }

    if (key === '_deliveryDetail') {
      value.matchRegions = await this.handleDeliveryRegions(value);
      this._billingDetail = this._billingIsDelivery ? value : this._billingDetail.first_name ? this._billingDetail : {};
    }

    if (key === '_payment') {
      scrollToSmooth(window.scrollY + 1, 0, 'instant');
      scrollToSmooth(window.scrollY - 1, 0, 'instant');
    }

    if (key === 'confirmFormEditing') {
      value = Object.assign(this.confirmFormEditing, value ?? {});
    }

    if (key === '_orderCount') {
      value = Object.assign(this._orderCount, value ?? {});
    }
    this[key] = value;

    if (key === 'createOrderLoading' && value === false) {
      document.body.removeAttribute('data-HideCustomerService');
    }

    cb && cb();
  };

  @computed
  get handleCalcOrderConfirmPayment() {
    return calcOrderConfirmPrice({
      orderCartItems: this.orderCartItems,
      cartExtraFee: this.cartExtraFee,
      _orderCount: this._orderCount,
    });
  }

  @computed
  get cartTotalPrice() {
    const { tax = {}, shippingFee } = this.cartExtraFee;

    let total = 0; // 购物车商品总价（存在分销折扣和八折折扣的）
    let amount = 0; // 购物车所有价格总价（total+加邮费税费）
    let itemsAmount = 0; // 购物车已选择的商品总价（只取现价
    let signal = '';
    let currency = '';
    let taxAmount = tax?.amount ? tax.amount : 0;
    let shippingAmount = shippingFee?.amount ? shippingFee.amount : 0;

    this.allCartItems.forEach((v) => {
      if (v.commodity && v.commodity.sale_state >= 0 && v.commodity.stock > 0 && v.price) {
        let count = v.count;
        currency = v.price.currency;
        signal = v.price.signal;
        if (v.selected) {
          total += handleItemPrice(v);
          itemsAmount += v.price?.amount * count;
          v.bind_services &&
            v.bind_services.forEach((o) => {
              total += count * o.price.amount;
              itemsAmount += count * o.price.amount;
            });
        }
      }
    });

    amount = total + taxAmount + shippingAmount;

    // 除了总价，单价变更，用于邮费比较
    if (isCalculating && previousCartTotalPrice) {
      previousCartTotalPrice.total = total;
      previousCartTotalPrice.itemsAmount = itemsAmount;
      return previousCartTotalPrice;
    }

    previousCartTotalPrice = { total, itemsAmount, amount, signal, currency };

    return { total, amount, itemsAmount, signal, currency };
  }

  /*
   *
   * @description 根据销售状态对购物车内容进行分类排序，绑定服务依旧在item内，confirm页面不展示被选中商品
   * @return {Object} list
   * @time: 2021/9/14 3:11 下午
   *
   */
  @computed
  get ShoppingCartItems() {
    if (!isBroswer) return [];
    const normalItems = [];
    const disabledItems = [];
    const items = this.root.app.isOrderConfirmPage ? this.unSelectedCartItems : this.allCartItems;
    items.forEach((v) => {
      if (v.commodity?.sale_state <= -1 || v.commodity?.stock <= 0) {
        // 缺货套餐只显示原价
        if (v?.origin_price?.amount) {
          v.price = v.origin_price;
          delete v.origin_price;
        }
        disabledItems.push(v);
      } else {
        normalItems.push(v);
      }
    });
    return [...normalItems, ...disabledItems];
  }

  /**
   *
   * @description 散列购物车所有item,包括绑定服务等，confirm页面不展示被选中商品
   * @return {Object} list
   * @time: 2021/9/14 3:11 下午
   *
   */
  @computed
  get ShoppingCartAllItems() {
    if (!isBroswer) return [];
    let items = this.cart_items;
    const list = [];
    if (this.root.app.isOrderConfirmPage) {
      items = this.notBeingCheckoutCartItems;
    }
    items.forEach((o) => {
      o.customType = CustomSkinHelper.getCustomType({ commodityId: o.commodityId });
      list.push(o);
      if (o.bind_services && o.bind_services[0]) {
        o.bind_services.forEach((bs) => {
          bs.isBindServices = true;
          list.push(bs);
        });
      }
    });

    return list;
  }

  /**
   *
   * @description 获取购物车全部数据
   */
  @computed
  get allCartItems() {
    return this.cart_items;
  }

  /**
   *
   * @description 获取购物车已选择的套餐列表
   */
  @computed
  get selectedCartItems() {
    return this.cart_items.filter((o) => o.selected);
  }

  /**
   *
   * @description 获取购物车未选择的套餐列表
   */
  @computed
  get unSelectedCartItems() {
    return this.cart_items.filter((o) => !o.selected);
  }

  /**
   * 当前页面的产品中是否有云存储产品
   * - 结账页取this.orderCartItems
   * - 二次支付页取simpleOrderDetail
   * - 订阅管理页直接返回true
   * - 其他页面取this.allCartItems
   * 存在为true，不存在为false，items为空为undefined。如有特殊需要使用 hasCloudService 方法
   */
  @computed
  get hasCloudServiceProduct() {
    const { isOrderResultPage, isOrderPayPage, isSubscriptionPage } = this.root.app;
    const { orderItemInfos } = this.simpleDetailOrder ?? {};
    const payPage = isOrderResultPage || isOrderPayPage;
    return isSubscriptionPage
      ? true
      : hasCloudService(
          this.root.app.isOrderConfirmPage ? this.orderCartItems : payPage ? orderItemInfos : this.allCartItems
        );
  }

  /**
   *
   * @desc 设置购物车数据，及更新至本地缓存
   * @param {Array} items 最新的数据列表
   * @param {boolean} needUpdateToRemote [false] 是否更新到数据库
   * @param {boolean} [awaitCalculatePrice=true] [true] 设置购物车商品 是否要阻塞计算价格流程（计算价格流程阻塞后会在邮费运费计算完成后再更新）
   * 假如set完之后不请求邮费运费，则不需要await
   */
  @action
  setCartItems = (items, needUpdateToRemote = false, awaitCalculatePrice = true) => {
    // T恤赠品不添加到本地缓存
    const itemCache = cloneDeep(items).map((o) => {
      delete o.itemInteriors;
      return o;
    });

    cache.set('cart_items', JSON.stringify(itemCache));
    isCalculating = awaitCalculatePrice;
    this.cart_items = items;
    if (needUpdateToRemote) updateCartToRemote(items);

    this.setCartItemsToCookie(items);
  };

  /**
   *
   * @description 初始化本地购物车数据到cookie
   */
  @action
  initCartItemsToCookie = () => {
    const insta_cart_items_cache = cache.getCookie('insta_cart_items');
    if (!insta_cart_items_cache) this.setCartItemsToCookie(this.cart_items);
  };

  /**
   *
   * @description 保存购物车数据至cookie, 共享官网用于购物车展示
   * @param {Array} items 购物车数据
   */
  @action
  setCartItemsToCookie = (items = []) => {
    try {
      const insta_cart_items = [];
      items?.forEach((o) => {
        insta_cart_items.push({ c: o.commodityId, n: o.count ?? 1 });
        o?.bind_services?.forEach((bs) => {
          insta_cart_items.push({ c: bs.id, n: bs.count ?? 1 });
        });
      });
      const Options = {
        expires: 360,
        domain: '.insta360.com',
      };
      if (this.root.app.isLocal) delete Options.domain;
      cache.setCookie('insta_cart_items', JSON.stringify(insta_cart_items), Options);
    } catch (err) {
      feishuRobotSend({
        text: ['封装购物车数据存储到cookie异常'],
        at: ['yhq'],
        err,
      });
    }
  };

  /**
   *
   * @description 根据页面获取购物车
   */
  @computed
  get currentPageCartItems() {
    const { isOrderConfirmPage } = this.root.app;
    if (isOrderConfirmPage) return this.orderCartItems;
    return this.selectedCartItems;
  }

  /**
   *  @description 获取结账页购物车数据 (无selected状态，结账页所有产品都结账)
   */
  @computed
  get orderCartItems() {
    const itemsSortByProductId = sortBy(this.order_cart_items, 'productId');
    return itemsSortByProductId;
  }

  /**
   *
   * @description 设置结账页数据，及更新至本地缓存
   * @param {Array} items 最新的数据列表
   */
  @action
  setOrderCartItems = (items) => {
    const { isCloudUpgradeOrderConfirmPage } = this.root.app;
    const itemsSortByProductId = sortBy(items, 'productId');
    cache.set(
      isCloudUpgradeOrderConfirmPage ? 'cloud_upgrade_items' : 'order_cart_items',
      JSON.stringify(itemsSortByProductId)
    );
    this.order_cart_items = itemsSortByProductId;
  };

  /**
   *
   * @description 设置云服务升商品本地缓存
   *
   */
  @action
  setCloudUpgradeItems = (data = []) => {
    cache.set('cloud_upgrade_items', JSON.stringify(data));
    this.order_cart_items = data;
  };

  @action
  setCartStickyBarVisible = (visible) => {
    this.cartStickyBarVisible = visible;
    this.root.meta.changePageToolBarPosition();
  };

  @computed
  get notBeingCheckoutCartItems() {
    let items = [];
    this.cart_items.forEach((item) => {
      const beingCheckout = this.order_cart_items.find((o) => o.commodityId === item.commodityId);
      if (!beingCheckout) {
        items.push(item);
      }
    });
    return items;
  }

  @computed
  get orderDeliveryDone() {
    return !!(this._deliveryDetail && this._deliveryDetail.first_name && this._deliveryDetail.phone);
  }

  @action
  downLoadInvioce = (orderNumber) => {
    if (!orderNumber) return;
    OrderTracker.orderDetailPage({ action: 'download_invoice' });
    return httpRequest({
      url: SERVER.API_SERVER_BASE + '/trade/order/getOrderInvoicePdfUrl',
      method: 'get',
      instaToken: true,
      params: {
        orderNumber,
      },
    });
  };

  @action
  changeCartItemSelected = (type, item, cb) => {
    const _allCartItems = cloneDeep(this.allCartItems);

    if (type === 'item') {
      _allCartItems.forEach((o) => {
        if (o.commodityId === item.commodityId) {
          o.selected = !o.selected;
        }
      });
    } else if (type === 'all') {
      _allCartItems.forEach((o) => {
        o.selected = item.target.checked;
        if (o.commodity.stock <= 0 || o.commodity.sale_state === -2) {
          o.selected = false;
        }
      });
    } else if (type === 'init') {
      _allCartItems.forEach((o) => {
        o.selected = true;
      });
    }

    this.setCartItems(_allCartItems);

    if (cb) {
      cb(_allCartItems);
    }
  };

  @action
  changeBatteryUnselected = (cb) => {
    const DANGEROUS_GOODS = [1]; // 如果不符合下单规则则将购物车中的「危险品」取消勾选。
    if (this.allCartItems.length === 0) {
      if (cb) {
        cb();
      }
    } else {
      const allCartItemsAfterUnselectBattery = this.allCartItems.map((o) => {
        if (DANGEROUS_GOODS.includes(o?.commodityAttribute)) {
          o.selected = false;
        }
        return o;
      });
      this.setCartItems(allCartItemsAfterUnselectBattery);
      cb?.();
    }
  };

  /**
   * 购物车商品处理逻辑，用于loadCartDetail的接口请求
   * @param {Array} items 处理commodity为指定接口格式，默认为selectCartItems, 使用loadCartDetail的参数格式[bind_services,id,number]
   * - 云存储商品数量只能为1
   * @param {String} from order 默认为order_cart_items | cart 默认为selectCartItems
   * @param {boolean} needPidParam
   * @return {Array} items
   */
  @action
  handleCartItems = (params = {}) => {
    const { items, from = 'cart', needPidParam = false } = params;
    let _items = [];

    // 默认取本地购物车数据
    // items判空：当前购物车有商品但是后端接口过滤掉了，这时候邮费运费的请求应该使用为空的items处理。即使items的值为空仍然应该使用，否则会走到下面的判断分支，拿当前的商品作为参数。
    const defaultItems =
      items && Array.isArray(items)
        ? cloneDeep(items)
        : from.includes('cart')
        ? this.selectedCartItems
        : this.order_cart_items;
    _items = defaultItems.map((o) => {
      const count = o.count || o.number;
      return {
        pid: needPidParam ? o.productId : undefined,
        id: o.commodityId || o.id,
        number: hasCloudService(o) ? 1 : count,
        bind_services: Array.isArray(o?.bind_services)
          ? o.bind_services
              .map((s) => ({
                pid: needPidParam ? o.productId : undefined,
                id: s.id || s.commodityId,
                number: o.count || o.number,
                type: s.serviceType || s.type,
              }))
              ?.filter((s) => o.bindClimbServices?.some?.((c) => c.serviceCommodityId === s.id)) // 过滤相机未绑定的增值服务（后台改变相机与增值服务的关联需要过滤掉用户在购物车中的旧增值服务
          : [],
      };
    });

    const queryId = fetchQueryParams('id');
    // 仅允许在购物车页执行该行为
    if (this.root.app.isCartPage && queryId) {
      const ids = queryId.split(',');
      ids.forEach((id) => {
        const cid = parseInt(id, 10);
        if (!isNaN(cid) && !FilterCartCids.includes(cid) && !isPreSaleCommodity({ cid })) {
          const exist = _items.filter((o) => o.id === cid)[0];
          if (!exist) {
            _items.push({
              id: cid,
              number: 1,
            });
          }
        }
      });
    }

    // 新加入了云存储商品，除了最后一个都删除
    if (this.root.app.isCartPage && hasCloudService(_items)) {
      let lastIndex = -1;
      _items.forEach((d, i) => {
        if (hasCloudService(d)) {
          lastIndex = i;
        }
      });

      _items = _items.filter((d, i) => (hasCloudService(d) ? i === lastIndex : true));
    }

    // 加入购物车之后把参数删掉，避免删除套餐后又重新加入购物车
    setTimeout(() => {
      historyReplaceState({}, handleQueryParams('id'));
    }, 200);

    return _items?.filter((o) => o.number > 0);
  };

  /**
   * @description 加载购物车所有产品数据，不考虑产品套餐的选择状态；
   */
  @action
  loadAllCartDetail = () => {
    if (isEmpty(this.allCartItems)) return;
    this.loadCartDetail({ params: { items: this.allCartItems }, action: 'allCartDetail' });
  };

  /**
   * @description 根据套餐数据，获取【非结账页】产品套餐列表详情、赠品、折扣等；
   * @param {Object} options 入参
   * @param {Function} options.callback 回调函数 加载完数据执行的回调
   * @param {function} emptyCallback items为空的回调
   * @param {Object} options.params
   * @param {Array} options.params.items 需要获取的套餐详情数据列表；
   * @param {String} options.params.[coupon_code_type] 折扣码类型
   * @param {Boolean} options.params.isFreeOrder 标记是否为免单类型，需要移除分销码（不送赠品），且下单时也需要移除分销码；
   *
   */
  @action
  loadCartDetail = (options = {}) => {
    const { params = {}, callback, emptyCallback, action } = options;
    const data = cloneDeep(params);
    data.items = this.handleCartItems({ items: params.items, needPidParam: true });

    if (!data?.items.length) {
      this.setStateValue('orderSceneType', 4);
      !action?.includes?.('load') && this.setCartItems([]);
      emptyCallback?.();
      this.root.app.setGlobalValue('G_GlobalLoading', false);
      return Promise.resolve(this.cart_items || []);
    }

    const { Q4BFGiftsCareForUS } = this.root.app.campaignWrap.campaignWrap;
    data.items = Q4BFGiftsCareForUS.filterCareFromLoadCartDetail({ items: data.items });

    this.setStateValue('loadingCartDetail', true);
    return httpRequest({
      url: SERVER.API_SERVER_TRADE + '/cart/loadCartDetail',
      method: 'post',
      data,
      removeInsrc: params.isFreeOrder,
    })
      .then(async (res) => {
        if (res.code === 0) {
          const localCartItems = this.allCartItems;
          const { commodityInfos, orderSceneType = 4 } = res.data;
          let newCartItems = [];
          this.setStateValue('orderSceneType', orderSceneType);

          /*
           *
           * 根据本地购物车和接口返回数据做处理
           * @param {object} localItem 本地购物车套餐数据（可能为空，注意后续判空逻辑）;
           * @param {object} dataItem 接口返回套餐数据（可能为空，注意后续判空逻辑）;
           *
           */
          const handleItems = ({ localItem, dataItem }) => {
            const payloadItem = data.items.find((d) => d.id === dataItem?.id); // 查询参数中的套餐
            // 1.存在本地购物车，但接口数据不存在
            if (localItem && !dataItem) {
              if (localItem.selected) return; // 页面选中套餐，过滤掉
              if (!localItem.selected) return newCartItems.push(localItem); // 目的在于把未选择的商品数据重新添加进购物车。
            }

            if (isEmpty(dataItem)) return;
            // 如果当前清空本地购物车且是删除操作，则阻断

            // 2. 存在本地购物车，且接口数据有返回
            // 3. 不存在本地购物车，但接口数据有返回
            const item = {};
            item.commodityId = dataItem.id;
            item.productId = dataItem.product;
            item.product = dataItem.product;
            item.displays = handleDisplays(dataItem.displays);
            item.thumb = item.displays[0]?.url_m;
            item.delivery_time_config = dataItem.delivery_time_config;
            item.info = dataItem.info;
            item.enabled = dataItem.enabled;
            item.price = dataItem.price;
            item.origin_price = dataItem?.origin_price ?? {};
            item.name = dataItem?.product_name ?? '';
            item.product_name = dataItem?.product_name ?? '';
            item.url_key = dataItem?.product_url_key;
            item.stock = dataItem.stock;
            item.order_buy_limit = dataItem.order_buy_limit;
            item.selected =
              payloadItem?.selected !== undefined
                ? payloadItem.selected
                : dataItem?.sale_state >= 0 && dataItem.stock > 0; // 优先使用查询item的selected
            item.commodityAttribute = dataItem.commodityAttribute;
            // 数量优先采用传入的查询套餐，再从本地取，比较库存和限购，取最小
            item.count = Math.min(
              payloadItem?.number ?? localItem?.count ?? 1,
              dataItem.stock,
              dataItem.order_buy_limit
            );
            item.count = item.count > 0 ? item.count : 1;

            item.commodity = {
              id: dataItem.id,
              sale_state: dataItem.sale_state,
              delivery_time_config: dataItem.delivery_time_config,
              info: dataItem.info,
              stock: dataItem.stock,
              order_buy_limit: dataItem.order_buy_limit,
              price: dataItem.price,
              origin_price: dataItem.origin_price,
              displays: item.displays,
            };
            // 增值服务类型
            if (dataItem?.serviceType) {
              item.serviceType = dataItem.serviceType;
            }
            // 相机拥有的增值服务
            item.bindClimbServices = dataItem?.bindClimbServices || [];
            item.bind_services = dataItem?.bind_services ?? [];
            item.bind_services.forEach((o) => {
              o.count = item?.count ?? 1; // 绑定服务的数量跟随套餐的数量
            });

            item.isOutOfStock = !isAvailableForSale(item.commodity);
            item.isNeedHidePrice = isNeedHidePriceProduct(item.product);

            newCartItems.push(item);
          };

          localCartItems.forEach((localItem) => {
            const dataItem = commodityInfos.filter((o) => o.id === localItem.commodityId)[0];
            handleItems({ localItem, dataItem });
          });

          commodityInfos.forEach((dataItem) => {
            const existed = newCartItems.filter((o) => o.commodityId === dataItem.id)[0];
            if (!existed) {
              handleItems({ dataItem });
            }
          });

          // 对Item做一些处理和过滤
          newCartItems = newCartItems
            .map((o) => {
              // 过滤不可销售绑定服务
              o.bind_services = [
                ...(o.bind_services.filter((s) => s.sale_state >= 0 && s.stock > 0 && s.enabled) ?? []),
              ];

              // 过滤相同绑定服务
              o.bind_services = uniq(o.bind_services, 'id');

              // 过滤当前国家没有价格配置的商品 | 不展示价格的商品
              if (!o.price) return false;
              if (o.isNeedHidePrice) return false;

              // 处理定制贴数据
              o = CustomSkinHelper.handleCartCache({
                cartItem: o,
                mobxOrder: this,
                LANG: this.root.area.LANG,
              });
              if (!o) return false;

              // 处理分销商折扣
              const item_reseller_discount = res.data.discounts[o.commodityId];
              o.reseller_discount_price = {};
              if (item_reseller_discount) {
                o.reseller_discount_price = {
                  amount: formatPrice(o.price.amount - item_reseller_discount.amount),
                  currency: o.price.currency,
                  signal: o.price.signal,
                };
              }

              // 移除导流地区产品
              const redirectData = ThirdPartyPlatformConfig.getRedirectData({ pid: o.productId, cid: o.commodityId });
              if (redirectData?.url) return false;

              // 过滤仅允许单独购买的产品
              if ([258].includes(o.productId)) {
                return false;
              }

              // 过滤care/延保
              if (Object.values(ExtraServiceType)?.includes(o?.serviceType)) {
                return false;
              }

              // 已订阅用户去除订阅商品
              if (this.root.app.isCloudServiceActive && hasCloudService(o)) {
                return false;
              }

              return o;
            })
            .filter((o) => !!o);

          // 获取套餐关联的T恤
          const { GiftTShirt } = this.root.app.campaignWrap.campaignWrap;
          newCartItems = await GiftTShirt.handleCartItemGifts({
            newCartItems,
            product: this.root.product,
          });

          // 如果赠品是flexicare，则不展示套餐名称
          if (res.data?.gifts) {
            res.data.gifts.forEach((gift) => {
              const giftServiceType = gift?.commodity?.serviceType;
              const isFlexiCareGift = giftServiceType === ExtraServiceType.CARE_PLUS;
              gift.commodity.info.name = isFlexiCareGift ? '' : gift.commodity.info.name;
            });
          }

          !action?.includes?.('load') && this.setCartItems(newCartItems); // 由并行接口调用，无需setItems

          callback && callback(newCartItems);
          this.gifts = res.data.gifts || [];
          this.root.app.setGlobalValue('G_GlobalLoading', false);
          this.setStateValue('loadingCartDetail', false);
          return newCartItems;
        }

        // 上报异常
        if (res.code !== 0) {
          this.setStateValue('loadingCartDetail', false);
          armsErrorSend('购物车loadCartDetail失败', {
            filename: 'Mobx Order',
            data: { request: data, response: res },
          });
        }
      })
      .catch((err) => {
        this.setStateValue('loadingCartDetail', false);
        this.root.app.setGlobalValue('G_GlobalLoading', false);
        armsErrorSend(err, { filename: 'loadCartDetail', data: { options } });
      });
  };

  /**
   * 获取配件八折优惠
   * @param from cart|order 调用页面，默认为购物车
   * @returns
   */
  @action
  getCloudServicesDiscounts = ({ from }) => {
    const newItems = this.handleCartItems({
      needPidParam: true,
      from,
    });

    if (!newItems.length) {
      return;
    }

    const couponCode = this._orderCount.coupon || this.root.app.insrc;

    const newItemsAfterFilter = this.handleCareServiceForUS({ type: 'filterCare', items: newItems });

    return httpRequest({
      url: SERVER.API_SERVER_BASE + '/cloud/getSubscribeDiscount',
      method: 'post',
      data: { items: newItemsAfterFilter, code: couponCode },
      instaToken: true,
    })
      .then((res) => {
        if (res.code === 0 && !isEmpty(res.data)) {
          return res.data.benefitDiscountItems || [];
        }
        return [];
      })
      .catch((e) => {
        armsErrorSend(e, {
          filename: 'orderMobx getCloudServicesDiscounts',
          data: { newItems, newItemsAfterFilter, action },
        });
        return [];
      });
  };

  /**
   * 内部调用多个接口 获取购物车商品数据，包括商品详情、分销价格、云服务配件八折优惠价
   * @param payload 参数同loadCartDetail或者loadOrderCartDetail
   * @param from cart|order 购物车获取详情, 结账页获取详情，默认cart
   * @param {Array} return newItems
   */
  @action
  getCartItemsDetail = (payload = {}, from = 'cart') => {
    const { country } = this.root.area;

    const fromCart = from === 'cart';
    const handleSetItems = (items) => {
      fromCart ? this.setCartItems(items) : this.setOrderCartItems(items);
    };

    // 处理参数
    if (!isEmpty(payload) && typeof payload === 'object') {
      payload.action = 'load';
    } else {
      payload = { action: 'load' };
    }

    /**
     * 请求数据
     * 1. 商品详情数据
     * 2. 云服务配件八折优惠折扣 —— 中美地区不请求
     */

    const requestFun = [fromCart ? this.loadCartDetail(payload) : this.loadOrderCartDetail(payload)];
    if (!diffCloudBenifitCountry().includes(country?.code)) {
      requestFun.push(this.getCloudServicesDiscounts({ from }));
    }

    return PromiseAllSettled(requestFun)
      .then(([resItems, discounts]) => {
        // res1为空整个请求失败，保持列表不变动或者设置为变动后的localCartItems值
        if (isEmpty(resItems) || !Array.isArray(resItems) || (Array.isArray(resItems) && resItems.length === 0)) {
          // 购物车列表为空时更新初始化本地缓存
          if (Array.isArray(resItems) && resItems.length === 0) {
            handleSetItems([]);
          }
          return [];
        }

        // 没有被选中的产品删除之前的八折优惠字段和本不该存在的origin_price字段
        if (fromCart) {
          this.allCartItems.forEach((d) => {
            delete d.accessoryDiscountPrice;
            if (d.origin_price?.amount <= d.price?.amount) {
              delete d.origin_price;
            }
          });
        }
        const newCartItems = resItems;

        // 没有权益信息，或者手动填写优惠券选择使用优惠券的场景 则直接返回，不用处理八折信息啦。
        if (isEmpty(discounts) || !Array.isArray(discounts)) {
          handleSetItems(newCartItems);
          return newCartItems;
        }

        newCartItems.forEach((item) => {
          const discount = discounts.find((d) => d.commodityId === item.commodityId);
          const noCouponDiscount = isEmpty(this._orderCount.couponDiscountsInfo)
            ? true
            : this._orderCount.couponDiscountsInfo.every((d) => d.commodityId !== item.commodityId);
          // 不存在优惠券折扣的情况才处理配件折扣
          if (discount && isEmpty(item.reseller_discount_price) && noCouponDiscount) {
            item.accessoryDiscountPrice = {
              // 是所有数量的折扣总价，因为会出现3个商品只有2个额度的情况。
              amount: discount.benefitDiscountTotalAmount,
              currency: item.price.currency,
              signal: item.price.signal,
            };

            if (isEmpty(item.origin_price)) item.origin_price = item.price;
          }
        });

        handleSetItems(newCartItems);
        return newCartItems;
      })
      .catch((e) => {
        armsErrorSend(e, { filename: 'getCartItemsDetail', data: { payload, from } });
      });
  };

  /**
   * 同步远端和本地的购物车信息，
   * 同步时机：首次进入页面和登录后 || 结账页登录后
   * @param isLogIn 是否是登录后同步
   * @returns
   */
  @action
  syncCartDetail = debounce(() => {
    const synced = cache.getCookie('insta_cart_synced');
    if (isEmpty(this.root.auth.isLoggedIn)) return;
    if (!isEmpty(this.root.auth.isLoggedIn) && synced === '1') return;
    httpRequest({
      url: SERVER.API_SERVER_TRADE + '/cart/getUserCartDetail',
      method: 'get',
      instaToken: true,
    }).then((res) => {
      if (res.code === 0) {
        cache.setCookie('insta_cart_synced', 1, { expires: 7 });
        if (res.data?.cartItems) {
          const remoteCartItems = res.data?.cartItems.filter((o) => !FilterCartCids.includes(o.commodityId)) || [];
          let new_items = [];

          // 遍历本地购物车数据
          this.allCartItems.forEach((item) => {
            const sameRemoteItem = remoteCartItems.find((d) => d.commodityId === item.commodityId);
            // 数量合并，判断库存和限购在loadcartdetail进行并重写
            new_items.push({
              ...item,
              number: item.count + (sameRemoteItem ? sameRemoteItem.number : 0),
              selected: !!item.selected,
            });
          });

          // 遍历远端数据, 找到本地不存在的产品，如果是云存储商品则以当前购物车的为准
          remoteCartItems.forEach((item) => {
            const isCloudProduct = hasCloudService(item);
            const inLocalItem = new_items.some(
              (d) => d.commodityId === item.commodityId || (isCloudProduct && hasCloudService(d))
            );
            if (!inLocalItem) {
              new_items.push({ ...item, number: item.number, selected: true });
            }
          });

          if (this.root.app.isOrderConfirmPage) {
            // 把正在结账的商品加入购物车列表
            this.orderCartItems.forEach((item) => {
              const inCartItems = new_items.find((d) => d.commodityId === item.commodityId);
              if (!inCartItems) {
                new_items.push({ ...item, number: item.number, selected: false });
              }
            });

            // 并把购物车列表未正在结账商品改为未选择
            new_items.forEach((item) => {
              const inPlaceOrderItems = this.orderCartItems.find((d) => d.commodityId === item.commodityId);
              item.selected = !!inPlaceOrderItems;
            });
          }

          // 处理数据，更新绑定服务数量
          new_items = uniq(
            new_items.map((o) => ({
              id: o.commodityId,
              number: o.number ?? o.count,
              selected: !!o.selected,
              bind_services:
                o.bind_services?.map((s) => ({
                  id: s.id ?? s.commodityId,
                  type: s.serviceType,
                  number: o.number ?? o.count,
                })) || [],
            })),
            'id'
          );

          // 过滤预售产品
          new_items = new_items.filter((o) => !isPreSaleCommodity({ cid: o.id }));

          const updateCb = (newCartItems) => {
            updateCartToRemote(newCartItems);
            const { isCartPage } = this.root.app;
            // 购物车loadCartDetail后需要计算邮费
            if (isCartPage) {
              const items = this.handleCartItems({ items: this.selectedCartItems });
              this.fetchOrderExtraFee({ items });
              this.calculateShippingFee({ items });
            }
          };
          // 购物车页刷新商品数据 & 折扣数据，其他页面只需要请求刷新购物车数据，不需要重计价格
          if (this.root.app.isCartPage) {
            this.getCartItemsDetail({ from: 'cart', params: { items: new_items }, callback: updateCb });
          } else {
            this.loadCartDetail({ params: { items: new_items }, callback: updateCb });
          }
        }
      }
    });
  }, 300);

  /**
   * @description 获取结账页产品列表详情；
   * @param {Object} params 入参
   * @param {String} params.coupon_code_type 折扣码类型 取值：[coupon_code， gift_card_code， reseller_code]
   * @param {Boolean} params.isFreeOrder 标记是否为免单类型，需要移除分销码（不送赠品）
   *
   */
  @action
  loadOrderCartDetail = (params = {}) => {
    const { isCloudUpgradeOrderConfirmPage } = this.root.app;

    return new Promise((resolve) => {
      const postData = Object.assign(params, {});

      // 不接受外部传参（结账过程不允许修改和变更套餐内容）
      // 默认从结账页本地缓存取，区分单独购买还是普通购买
      postData.items = this.handleCartItems({ from: 'order', needPidParam: true });

      if (!postData.items.length) return;

      postData.items = this.handleCareServiceForUS({ type: 'filterCare', items: postData.items });
      const removeInsrc = params.isFreeOrder || isCloudUpgradeOrderConfirmPage;

      httpRequest({
        url: SERVER.API_SERVER_TRADE + '/cart/loadCartDetail',
        method: 'post',
        data: postData,
        removeInsrc,
      }).then(async ({ code, data }) => {
        if (code !== 0) {
          this.order_cart_items = [];
          this.orderConfirmGifts = [];
          return;
        }
        if (code === 0) {
          let newItems = [];
          const { orderSceneType = 4 } = data;
          this.setStateValue('orderSceneType', orderSceneType);
          // 处理当前结账页数据与接口返回数据
          const handleItems = ({ localItem, dataItem }) => {
            // 接口没有返回该套餐，则过滤掉
            if (!dataItem) return;

            const item = {};
            item.commodityId = dataItem.id;
            item.productId = dataItem.product;
            item.product = dataItem.product;
            item.displays = handleDisplays(dataItem.displays);
            item.thumb = item?.displays[0]?.url_m ?? '';
            item.delivery_time_config = dataItem.delivery_time_config;
            item.info = dataItem.info;
            item.enabled = dataItem.enabled;
            item.price = dataItem.price;
            item.origin_price = dataItem?.origin_price ?? {};
            item.name = dataItem?.product_name ?? '';
            item.product_name = dataItem?.product_name ?? '';
            item.url_key = dataItem?.product_url_key;
            item.count = Math.min(localItem?.count ?? 1, dataItem.stock, dataItem.order_buy_limit);
            item.count = item.count > 0 ? item.count : 1;
            item.stock = dataItem.stock;
            item.order_buy_limit = dataItem.order_buy_limit;
            item.sale_state = dataItem.sale_state;
            item.commodity = {
              id: dataItem.id,
              sale_state: dataItem.sale_state,
              delivery_time_config: dataItem.delivery_time_config,
              info: dataItem.info,
              stock: dataItem.stock,
              order_buy_limit: dataItem.order_buy_limit,
              price: dataItem.price,
              displays: item.displays,
            };

            // 处理分销商折扣
            item.reseller_discount_price = {};
            if (!isEmpty(dataItem.discounts)) {
              const item_reseller_discount = dataItem.discounts[item.commodityId];
              if (item_reseller_discount) {
                item.reseller_discount_price = {
                  amount: formatPrice(dataItem.price.amount - item_reseller_discount.amount),
                  currency: item.price.currency,
                  signal: item.price.signal,
                };
              }
            }

            // 处理增值服务，过滤不可销售绑定服务，给增值服务绑定数量
            item.bind_services = [];
            if (dataItem?.bind_services) {
              dataItem.bind_services = dataItem.bind_services.filter(
                (s) => s.sale_state >= 0 && s.stock > 0 && s.enabled
              );
              item.bind_services = dataItem.bind_services;
              // 相机拥有的增值服务
              item.bindClimbServices = dataItem?.bindClimbServices || [];
              item.bind_services.forEach((o) => {
                o.count = item?.count ?? 1; // 绑定服务的数量跟随套餐的数量
              });
            }
            // 增值服务类型
            if (dataItem?.serviceType) {
              item.serviceType = dataItem.serviceType;
            }

            item.isOutOfStock = !isAvailableForSale(item.commodity);
            item.isNeedHidePrice = isNeedHidePriceProduct(item.product);
            newItems.push(item);
          };

          this.order_cart_items.forEach((localItem) => {
            const dataItem = data.commodityInfos.find((o) => o.id === localItem.commodityId);
            handleItems({ localItem, dataItem });
          });
          // 对Item做一些处理和过滤
          newItems = newItems.filter((item) => {
            // 过滤当前国家没有价格配置的商品 | 不展示价格的商品
            if (!item.price) return false;
            if (item.isNeedHidePrice) return false;

            if (!isCloudUpgradeOrderConfirmPage && this.root.app.isCloudServiceActive && hasCloudService(item)) {
              // 已订阅用户去除订阅商品
              return false;
            }
            if (item.isOutOfStock) return false; // 缺货商品

            item = CustomSkinHelper.handleCartCache({
              cartItem: item,
              mobxOrder: this,
              LANG: this.root.area.LANG,
            });
            if (!item) return false;

            // 处理分销商折扣
            const item_reseller_discount = data.discounts[item.commodityId];
            item.reseller_discount_price = {};
            if (item_reseller_discount) {
              item.reseller_discount_price = {
                amount: formatPrice(item.price.amount - item_reseller_discount.amount),
                currency: item.price.currency,
                signal: item.price.signal,
              };
            }

            // 移除导流地区产品
            const redirectData = ThirdPartyPlatformConfig.getRedirectData({
              pid: item.productId,
              cid: item.commodityId,
            });
            if (redirectData?.url) return false;

            return true;
          });
          // 从getCartItemsDetail进入该方法无需保存到本地
          !params.action?.includes?.('load') && this.setOrderCartItems(newItems);

          // 组装T恤放在赠品最后
          const { GiftTShirt } = this.root.app.campaignWrap.campaignWrap;
          const giftTShirtItems = await GiftTShirt.handleOrderItemGifts({
            orderConfirmItems: newItems,
            product: this.root.product,
          });

          // 处理赠品
          this.orderConfirmGifts = [...(data?.gifts ?? []), ...(giftTShirtItems ?? [])];

          resolve(newItems);
        }
      });
    });
  };

  /**
   *
   * @description 创建订单前置校验，校验失败则return中断执行
   * @param {String} type 校验数据类型
   * @return {type} Promise
   * @time 2022/10/13 22:05
   *
   */
  @action
  perValidateCreateOrder = (type) => {
    const {
      country: { code },
    } = this.root.area;
    const isCN = code === 'CN';
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      let res = true;
      switch (type) {
        case 'delivery':
          if (isCN) this.validateRegions();
          const validateContact = await this.validateOrderForm('contact');
          const validateDelivery = await this.validateOrderForm('delivery');

          if (!validateContact) {
            message.warn(LANG['common.form.deliveryAddress_warningMsg']);
            scrollToEle('#userContactForm');
            res = false;
            break;
          } else if (!validateDelivery || !this._deliveryDetail.country) {
            message.warn(LANG['common.form.deliveryAddress_warningMsg']);
            scrollToEle('div[data-delivery-item-active="true"]');
            res = false;
            break;
          }

          if (isCN && this._deliveryDetail.matchRegions) {
            if (this._deliveryDetail.matchRegions.needUpdateRegions) {
              message.warn('请检查您的收货信息是否正确');
              scrollToEle('div[data-delivery-item-active="true"]');
              res = false;
              break;
            }
            // 不阻断用户下单
            // if (this._deliveryDetail.matchRegions.needUpdateDistrict){
            //   message.warn('为确保订单正常配送，请更新并确认您的收货地区是否正确');
            //   scrollToEle('div[data-delivery-item-active="true"]');
            //   res=false;
            //   break;
            // }
            // 不阻断用户下单
            // if (this._deliveryDetail.matchRegions.needUpdateMatchRegions){
            //   message.warn('为确保订单正常配送，请确认或修改您的收货地区是否正确');
            //   scrollToEle('div[data-delivery-item-active="true"]');
            //   res=false;
            //   break;
            // }
          }

          if (this._deliveryDetail.needUpdateProvince) {
            if (this._deliveryActionCallback) {
              this._deliveryActionCallback('edit', this._deliveryDetail);
              res = false;
              break;
            } else {
              message.warn(LANG['order.shipping.deliveryProvinceUpdateMsg']);
              scrollToEle('#deliveryContainer');
              res = false;
              break;
            }
          }

          // 不阻断用户下单
          // 区号校验
          // if (!this._deliveryDetail.phone_code) {
          //   message.warn(LANG['common.form.phoneCode_warningMsg']);
          //   scrollToEle('#deliveryContainer');
          //   res=false;
          //   break;
          // } else {
          //   const val = this._deliveryDetail.phone_code.replace('+', '');
          //   const validatorPhoneCode = validator({
          //     type: 'phoneCode',
          //     val,
          //     extra: { allCountry: this.root.area.allCountry },
          //   });
          //   if (!validatorPhoneCode) {
          //     message.warn(LANG['common.form.phoneCode_warningMsg']);
          //     scrollToEle('#deliveryContainer');
          //   res=false;
          //   break;
          //   }
          // }

          resolve(true);
          break;

        case 'billing':
          if (isCN || this._billingIsDelivery) {
            resolve(true);
            return;
          }

          const validateBilling = await this.validateOrderForm('billing');
          if (!validateBilling || !this._billingDetail.country) {
            message.warn(LANG['common.form.billingAddress_warningMsg']);
            res = false;
            break;
          }

          // 城市校验 港澳台的旧数据没有city导致订单提交报错
          if (['HK', 'MO', 'TW'].includes(CountryCode) && isEmpty(this._deliveryDetail.city)) {
            message.warn(LANG['common.form.addressCity_msg']);
            scrollToEle('#billingContainer');
            res = false;
            break;
          }

          resolve(true);
          break;

        case 'payment':
          if (!this._payment.type) {
            message.warning(LANG['common.form.paymentMethod_warningMsg']);
            scrollToEle('#paymentsContainer');
            res = false;
            break;
          }
          if (this._payment.type === 'creditCard' && !this._payment.cardData) {
            message.warn(LANG['common.tips.clickToSaveCreditCardInfo']);
            scrollToEle('#creditCardContainer');
            res = false;
            break;
          }
          resolve(true);
          break;

        case 'invoice':
          if (this._orderInvoice.type !== 'none' && !this._orderInvoice.done) {
            message.warning('请先保存发票信息！');
            scrollToEle('#invoiceContainer');
            res = false;
            break;
          }
          resolve(true);
          break;
        case 'checkOrderTaxForMX':
          if (this.checkOrderTaxForMX()) {
            message.warn('Create order error, Please contact out customer service!');
            res = false;
            break;
          }
          resolve(true);
          break;
        case 'subscription':
          if (this.hasCloudServiceProduct && !this.form.subscription.agreementVersion) {
            res = false;
            break;
          }
          resolve(true);
          break;
        default:
      }

      if (res === false) {
        PaymentTracker.confirmPageTracker({ action: 'review_placeOrder', label: 'failed' });
        return false;
      } else if (res) {
        resolve(true);
      }
    });
  };

  /**
   * 创建订单：步骤：1、创建订单。2、调起支付流程
   * @param {object} order
   * @param {boolean} createOrderOptions.immediateToPay 默认为true 是否立即调起支付流程
   */
  @action
  createOrder = async (order, createOrderOptions = {}) => {
    const { isGuest, isLoggedIn, guest: guestEmail, email: userEmail } = this.root.auth;
    const { isMobile, isCloudServiceActive, isCloudUpgradeOrderConfirmPage } = this.root.app;
    const {
      country: { code: countryCode },
    } = this.root.area;
    const { immediateToPay = true } = createOrderOptions;

    // 避免频繁调用
    if (this.createOrderLoading) return;

    let tempOrderData = {
      customType: '',
      customItemImageInfos: [],
    };

    const getOrderEndPoint = () => {
      //  order_end_point  1: 桌面端；2: 移动端; 3: 小程序端； 4: App端
      let order_end_point = 1;
      if (cache.getCookie('insta_app_name')) {
        order_end_point = 4;
      } else if (isMobile) {
        order_end_point = 2;
      }
      return {
        order_end_point,
      };
    };

    const getOrderBilling = () => {
      const billing = {
        ...(this._billingIsDelivery
          ? bindProvinceCode(this.ProvinceOceanCodes, this._deliveryDetail, 'billing')
          : bindProvinceCode(this.ProvinceOceanCodes, this._billingDetail, 'billing')),
      };
      // 删除多余的属性
      billing.phoneCodeArea && delete billing.phoneCodeArea;
      billing.phone_code_area && delete billing.phone_code_area;
      billing.delivery_tax && delete billing.delivery_tax;

      return { billing };
    };

    // 封装customItemImageInfos参数
    tempOrderData = CustomSkinHelper.handleOrderCreate({ orderItems: order.items, tempOrderData, mobxOrder: this });

    let data = {
      contact_email: (isGuest ? guestEmail : userEmail) || getUserAccount('email') || this._deliveryDetail?.email, // 联系邮箱
      items: order.items, // 结账页购物车数据
      cloud: order.cloud || false, // 免费赠送云存
      coupon_code: this._orderCount.couponType === 'coupon_code' ? this._orderCount.coupon : '', // str 优惠码
      gift_card_code: this._orderCount.couponType === 'gift_card_code' ? this._orderCount.coupon : '', // str 代金券
      inscp: window._ist('get', { key: 'params' }) ? window._ist('get', { key: 'params' }).ref : null, // str 流量来源
      subscribe: this._orderAgreementCheckState.insta360Subscribe, // bool 是否订阅
      prePaymentChannel: this._payment.type,
      invoice: {
        // obj 发票信息
        type: this._orderInvoice.type,
        ...this._orderInvoice.info, // obj 发票信息
      },
      // 买了云服务就必须要传此参数，协议会更新
      orderExtra: !this.hasCloudServiceProduct ? undefined : this.form.subscription,
      delivery: bindProvinceCode(this.ProvinceOceanCodes, { ...this._deliveryDetail }),
      delivery_id: this._deliveryDetail?.id ?? undefined,
      customItemImageInfos: tempOrderData.customItemImageInfos,
      orderPromotion: {}, // 分销参数
      ...getOrderBilling(), // obj 账单地址
      ...getOrderEndPoint(), // int 终端
      ...(this.hasCloudServiceProduct
        ? isCloudServiceActive // 云服务订阅类型 1——首次订阅 3——升级
          ? {
              cloudSubscribeType: 3,
            }
          : { cloudSubscribeType: 1 }
        : {}),
    };

    if (cache.getCookie('insrc')) {
      ['utm_source', 'utm_medium', 'utm_campaign'].forEach((key) => {
        const val = cache.getCookie(`insta360.${key}`);
        if (val) {
          // 转驼峰命名
          const field = key
            .split('_')
            .map((s, i) => (i === 1 ? s.charAt(0).toUpperCase() + s.slice(1) : s))
            .join('');
          data.orderPromotion[field] = val;
        }
      });
    }
    // 单独购买延保、care
    if (this._orderCount.isOnlyBindServiceOrder) {
      if (!this.root.product.InsuranceService.serial) {
        message.error(LANG['order.confirm.no_serial_number']);
        return;
      }
      data.device_serial = this.root.product.InsuranceService.serial; // str 保险绑定机器序列号
      // care超期购买校验，当前购买的是对应sn码则加备注
      const careExtendInfo = this.root.product.handleInsuranceService('getCareExtendInfo');
      if (careExtendInfo?.sn === data.device_serial) {
        data.remark = careExtendInfo.remark;
      }
    }

    // 组装T恤赠品到itemInteriors字段 && 用户选择T恤偏好 remark 买家备注
    const { GiftTShirt } = this.root.app.campaignWrap.campaignWrap;
    data = await GiftTShirt.handleOrderCreateGifts({ orderData: data, product: this.root.product });

    await this.perValidateCreateOrder('delivery');
    await this.perValidateCreateOrder('billing');
    await this.perValidateCreateOrder('payment');
    await this.perValidateCreateOrder('invoice');
    await this.perValidateCreateOrder('checkOrderTaxForMX');

    const res = await this.perValidateCreateOrder('subscription');
    if (!res) {
      // 缺少订阅参数阻断
      armsErrorSend('order create validate fail', { filename: 'cloudService no agreementVersion' });
      return Promise.resolve({ code: 'subscription_payload_error' });
    }

    this.setStateValue('createOrderLoading', true);

    PaymentTracker.confirmPageTracker({
      action: 'review_placeOrder',
      label:
        this.cartExtraFee?.shippingFee?.amount > 0
          ? `success_${this.cartExtraFee.shippingFee.signal}${this.cartExtraFee.shippingFee.amount}`
          : 'success_Free',
    });
    OrderTracker.orderContactInfo({ action: 'isLoggedIn', label: isGuest ? 'guest' : 'user' });

    if (data?.delivery?.hasOwnProperty('matchRegions')) delete data.delivery.matchRegions;
    if (data?.billing?.hasOwnProperty('matchRegions')) delete data.billing.matchRegions;

    // US - 封装care增值服务赠品
    data.otherItems = this.handleCareServiceForUS({ type: 'handleOrderCreateItems', items: data.items });

    // os上报
    try {
      openSearchOrderReport({
        items: this.orderCartItems,
        total: this.handleCalcOrderConfirmPayment.total,
      });
    } catch (err) {
      console.error(err);
    }

    const prevEmailData = (isGuest ? guestEmail : userEmail) || getUserAccount('email');
    if (!prevEmailData) {
      try {
        const guestProfile = JSON.parse(window.sessionStorage.getItem('guestProfile') || '{}') ?? {};
        feishuRobotSend({
          text: [
            `这个订单没有邮箱 & 已经使用地址邮箱兜底${data.contact_email}`,
            `isGuest: ${isGuest}; guestEmail:${guestEmail}; userEmail:${userEmail}`,
            `getUserAccount('email'): ${getUserAccount('email')}; guestProfile: ${JSON.stringify(
              guestProfile
            )}; guestEmail:${guestProfile?.email}`,
          ],
          at: ['dxy'],
        });
      } catch (e) {
        console.error(e);
      }
    }

    // PushEngage停止推送通知
    ThirdPartyTracker.PushEngageTracker.checkOut({ language: this.root.area.country?.X_Language });
    // 上述条件全部满足时,点击按钮正常下单,触发checkout事件,暂停弹窗推送
    return new Promise((resolve) => {
      httpRequest({
        url: SERVER.API_SERVER_TRADE + '/order/create',
        method: 'post',
        data,
        instaToken: !!isLoggedIn,
        removeInsrc: isCloudUpgradeOrderConfirmPage || this._orderCount.isFreeOrder,
        X_Language: this.root.area.country?.language, // 国家码和语言以当前页面为准，避免切换新页面和地区后，造成下单抛21007国家不匹配错误。
        X_Country_Code: this.root.area.country?.code,
      })
        .then(async (res) => {
          if (res.code === 0) {
            this.setStateValue('detailOrder', res.data.order);

            // 标记下完单需要清空数据（下次刷新页面会执行删除动作）
            cache.setCookie('need_reset_cart', 1);

            countryCode === 'US' &&
              ThirdPartyTracker.VWOTracker('insta360Purchase', {
                items: this.orderCartItems,
                orderNumber: res.data.order.orderNumber,
                orderPaymentInfo: this._orderCount,
                // 送care
                care: this.careServiceOnOrderCartItems,
              });

            this.root.app.trackTiktok('PlaceAnOrder', {
              data: this.orderCartItems,
              total: this._orderCount.total,
              order_number: res.data.order.orderNumber,
              delivery: this._deliveryDetail,
            });
            this.root.app.trackFb('InitiateCheckout', {
              data: this.orderCartItems,
              total: this._orderCount.total,
              order_number: res.data.order.orderNumber,
              delivery: this._deliveryDetail,
            });

            // 上报支付方式
            EcommerceTracker.addPaymentInfo({
              currency: this._orderCount.currency,
              value: this._orderCount.total,
              payment_type: this._payment?.type,
              items: this.orderCartItems,
              coupon: this._orderCount.coupon,
            });

            // 订单创建成功，移除定制信息
            tempOrderData.customItemImageInfos.forEach((o) => {
              CustomSkinHelper.handleCustomCache({
                customType: o.type,
                action: 'reset',
              });
            });

            // 生成以旧换新订单则删除内存
            if (this._orderCount.tradeUpCouponAmount > 0) {
              this.root.tradeup.handleTradeupCache({ type: 'remove' });
            }

            // 单独购买虚拟服务的订单删除缓存信息
            if (this._orderCount.isOnlyBindServiceOrder) {
              cache.remove('i_s_d_s');
              cache.remove('care_extend_info');
            }

            // 创建完订单插入一个已创建页面防止错误返回，这里只能变更路径不能变更页面样式，否则spb组件被摧毁接收不到回调
            if (window.history.pushState) {
              window.history.pushState({ step: 3 }, '', '/order/confirm/created');
            }

            // 如果立即支付，则进行如下处理（跳转页面）
            if (immediateToPay) {
              this.toPay();
            } else {
              // 如果不是立即支付，取消创建订单的loading
              this.setStateValue('createOrderLoading', false);
            }
          } else {
            this.root.auth.dataCollect({
              event: 'CreateOrderInstaException',
              data: { res, request: data },
            });

            armsErrorSend('下单失败 StoreOrderCreate', {
              filename: 'Mobx Order',
              data: { request: data, response: res },
            });
            this.setStateValue('createOrderLoading', false);
            if ([21402, 21403, 21404, 21405, 21407, 21408].includes(res.code)) {
              message.warning(LANG['common.tips.couponCode_error'], 5);
            } else if (res.code === 21116) {
              message.warning(LANG['common.tips.create_order_21116_error'], 5);
            } else if ([23001, 23002, 23003, 23004, 23005].includes(res.code)) {
              message.warning(LANG['order.confirm.tax_address_order_failed'], 5);
            } else if ([105010001].includes(res.code)) {
              message.warning(LANG['order.confirm.failModal.order.confirm.failModal.us_five_island_fail_msg'], 5);
            } else if ([16001].includes(res.code)) {
              // 已存在订阅 云服务错误无法购买
              return resolve(res);
            } else {
              message.warning(LANG['common.tips.create_order_other_error'], 5);
            }
          }

          return resolve(res);
        })
        .catch((err) => {
          message.warning(LANG['common.tips.create_order_other_error'], 5);
          this.setStateValue('createOrderLoading', false);
          this.root.auth.dataCollect({
            event: 'CreateOrderError',
            data: { err, request: data },
          });
        });
    });
  };

  /**
   *
   * @description 根据支付类型调用相应支付函数
   * @param payload 支付方式判断入参
   * @param payload.adaptiveType 当前支付是什么类型场景（比如云存主动支付 / 或者其他的场景）
   */
  @action
  toPay = async (payload) => {
    const {
      _orderCount: { isFreeOrder },
      _payment: { type },
      detailOrder: { order_number: orderNumber }, // 结账页响应体
      simpleDetailOrder: { orderId: rePayId, orderNumber: rePayOrderNumber, area, orderPaymentInfo },
      handleCalcOrderConfirmPayment,
    } = this;
    const { isOrderConfirmPage } = this.root.app;
    const { LANG } = this.root.area;
    const payMode = this.hasCloudServiceProduct ? 2 : 1;
    this.setStateValue('_beingPaid', true);
    this.setStateValue('createOrderLoading', false);

    const canPay = () => {
      return new Promise((resolve) => {
        const canPayOrderNumber = orderNumber || rePayOrderNumber;

        if (isOrderConfirmPage) {
          resolve(true);
          return;
        }

        this.getSimpleOrderInfo({
          orderNumber: canPayOrderNumber,
          callback: (res) => {
            if (res?.data?.simpleOrderInfo?.orderState === ORDER_STATE.未支付) {
              // 只有未支付能发起支付，否则都跳转结果页
              resolve(true);
            } else {
              feishuRobotSend({
                text: [
                  `【订单支付异常：状态不是未支付】`,
                  `订单号：${canPayOrderNumber}`,
                  `状态码：${res?.data?.simpleOrderInfo?.orderState}`,
                ],
                at: ['dxy'],
              });
              // 所有状态不是未支付的订单，都跳转结果页
              browserPushUrl(`/order/result/${encodeURIEncryptFE(canPayOrderNumber)}`);
            }
          },
        });
      });
    };

    switch (type) {
      case 'paypal':
        canPay().then(() => {
          httpRequest({
            url: `${SERVER.API_SERVER_PAYMENT}/paypal/paypal`,
            method: 'POST',
            data: {
              orderNumber,
              payMode,
            },
          })
            .then((res) => {
              if (res.code !== 0) {
                browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
                return;
              }
              window.location.href = res.data.payUrl;
            })
            .catch(() => {
              browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
            });
        });

        break;
      case 'alipay':
        canPay().then(() => {
          window.location.href = `${SERVER.API_SERVER_PAYMENT}/${type}/${type}?orderNumber=${orderNumber}`;
        });
        break;
      case 'weiXin':
        canPay().then(() => {
          if (isWechat()) {
            window.location.href = `${SERVER.API_SERVER_PAYMENT}/wechat/${type}?orderNumber=${orderNumber}`;
          } else {
            browserPushUrl(`/order/weixinpay/${encodeURIEncryptFE(orderNumber)}`);
          }
          this.setStateValue('_beingPaid', false);
        });
        break;
      case 'transfer':
        if (isFreeOrder) {
          browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
        } else {
          canPay().then(() => {
            browserPushUrl(`/order/transfer/${encodeURIEncryptFE(orderNumber)}`);
          });
        }
        this.setStateValue('_beingPaid', false);
        break;
      case 'creditCard':
        canPay().then(() => {
          // 主动续费
          if (payload?.adaptiveType === 'updatePay') {
            this.submitCloudSubscribeCreditCard(payload);
          } else {
            this.submitCreditCard(payload);
          }
        });
        break;
      case 'sofortBanking':
      case 'ideal':
      case 'kakaoPay':
      case 'naverPay':
      case 'korCreditCard':
        canPay().then(() => {
          this.submitLocalCreditCard(type);
        });
        break;
      case 'klarna':
        // 新创建订单 调用晚个1s左右。订单刚刚创建完成，有些数据还没有落到数据库。
        canPay().then(() => {
          if (rePayId) {
            this.submitLocalPayment(type, { orderNumber, payMode });
          } else {
            setTimeout(() => {
              this.submitLocalPayment(type, { orderNumber, payMode });
            }, 1000);
          }
        });
        break;
      case 'ApplePay':
        // 从结账页唤醒支付
        if (isOrderConfirmPage) {
          ApplePay.wakeUpPayment({
            isOrderConfirmPage,
            orderNumber,
            orderState: this,
            LANG,
            payMode,
          });
        }

        // 从二次支付页唤醒支付, 先创建会话，后再查状态决定是否唤起支付
        if (!isOrderConfirmPage) {
          await ApplePay.requestSession({
            countryCode: area,
            currencyCode: orderPaymentInfo?.currency,
            totalAmount: orderPaymentInfo?.totalAmount,
            balancePrice: orderPaymentInfo?.amount,
            shippingFee: orderPaymentInfo?.shippingFee,
            couponFee: orderPaymentInfo?.couponFee,
            taxFee: orderPaymentInfo?.taxFee,
            LANG,
            orderState: this,
            isOrderConfirmPage: false,
          });
          canPay().then(() => {
            ApplePay.wakeUpPayment({
              isOrderConfirmPage,
              orderNumber: rePayOrderNumber,
              orderState: this,
              LANG,
              payMode,
            });
          });
        }
        break;
      case 'google_pay':
        canPay().then(() => {
          GooglePay.wakeUpPaymentClient({
            countryCode: area,
            currencyCode: isOrderConfirmPage ? handleCalcOrderConfirmPayment.currency : orderPaymentInfo?.currency,
            totalAmount: isOrderConfirmPage ? handleCalcOrderConfirmPayment.total : orderPaymentInfo?.totalAmount,

            balancePrice: isOrderConfirmPage ? handleCalcOrderConfirmPayment.items : orderPaymentInfo?.amount,
            shippingFee: isOrderConfirmPage ? handleCalcOrderConfirmPayment.ship : orderPaymentInfo?.shippingFee,
            couponFee: isOrderConfirmPage ? 0 - handleCalcOrderConfirmPayment.discount : orderPaymentInfo?.couponFee,
            taxFee: isOrderConfirmPage
              ? handleCalcOrderConfirmPayment.tax || handleCalcOrderConfirmPayment.salesTax
              : orderPaymentInfo?.taxFee,

            orderNumber: orderNumber || rePayOrderNumber,
            orderState: this,
            LANG,
            isOrderConfirmPage,
            payMode,
          });
        });
        break;
      default:
        document.body.removeAttribute('data-HideCustomerService');
    }

    // 移动端 历史记录返回时不会刷新页面，手动更新页面步骤为最新
    if (this.currentConfirmStep === 2 && window.location.href.includes('/order/confirm/created')) {
      setTimeout(() => {
        scrollToSmooth(0);
        this.changeConfirmStep(3);
      }, 2000);
    }
  };

  /**
   * forter风控支付前置验证接口，信用卡支付 / 云服务重订阅前查询（为了获得风控结果参数），本土信用卡支付前不用查询
   * ### return Promise({status:'reject'|'approve',code,result}) reject就是forter拒绝，通过是approve
   * @param {object} payload
   * @param {string} type subscription 订阅
   *  */
  @action
  forterAdaptiveAuth = (payload, type) => {
    const isSubscription = type === 'subscription';

    let data = {
      ...payload,
      forterTokenCookie: window.localStorage.getItem('ftr-token'),
    };
    const collectForterResponse = ({ status, response }) => {
      this.root.auth.dataCollect({
        event: type + 'ForterAdaptiveAuth',
        data: { status, response, request: data },
      });
    };

    return httpRequest({
      url: isSubscription
        ? SERVER.API_SERVER_CLOUD + '/subscribeForterAdaptiveAuth'
        : SERVER.API_SERVER_TRADE + '/payment/forterAdaptiveAuth',
      method: 'post',
      data,
      instaToken: true,
    })
      .then((res = {}) => {
        const { code, data = {} } = res;
        const result = { status: 'reject', code, result: data.result };
        switch (true) {
          case code !== 0:
          case !data:
          case !data.result:
          case forterResult.reject.includes(data.result):
            // forter拒绝
            break;
          case forterResult.pass.includes(data.result):
            // forter通过，包含（需要验证和豁免的具体情况由回调判断，可参考payment.config.js 中 forterResult）
            result.status = 'approve';
            break;
          default:
            break;
        }

        collectForterResponse({
          status: res.code === 0 ? 'success' : 'fail',
          response: res,
        });
        return result;
      })
      .catch((err) => {
        collectForterResponse({
          status: 'fail',
          response: err,
        });
        armsErrorSend(err, { filename: 'forterAdaptiveAuth', data: { payload, type } });
        return { status: 'reject' };
      });
  };

  /**
   *
   */

  // 结果页面获取支付错误原因和上次支付渠道
  @action
  getPaymentFailMsg = (params) => {
    return httpRequest({
      url: SERVER.API_SERVER_TRADE + '/payment/getOrderPayChannel',
      params,
    }).then((res) => {
      if (res.code === 0) {
        return res.data?.failureReason ?? {};
      }
      return {};
    });
  };

  /**
   * 查询地址是否符合税率计算要求，有三种场景：1. 地址查询完全失败。2. 地址可被纠正 3.正确
   * @param delivery 传入地址信息，无则取_deliveryDetail 【delivery直接在引用对象上修改值】
   *
   * @return {promise|null} delivery US增加delivery_tax对象（其他国家没有），对象内字段如下：
   * -uniqueKey 地址是否已取得税费缓存唯一标识。 -validateAddress 地址请求是否成功 -validateTax 税费请求是否成功
   * -salesTax 此地址 / 此价格 / 此商品的消费税金额纪录 -confirmDelivery 纠正地址  -isConfirmDelivery 是否已经确定过纠正弹窗
   * -canReCheckTax 当修改过优惠券或者选择支付有折扣后，切换地址，此时允许重新发起请求。
   * -validateAddressError validateTaxError
   */
  getAddressTaxValidate = (_delivery) => {
    const { orderCartItems, _deliveryDetail, _orderCount } = this;
    const {
      country: { code },
    } = this.root.area;
    const delivery = _delivery || _deliveryDetail;

    this._orderCount.salesTax = delivery.delivery_tax?.salesTax ?? 0;
    if (!['US', 'CA'].includes(code)) return;
    if (_orderCount.isFreeOrder) return;
    if (isEmpty(orderCartItems) || !delivery) return;

    if (!delivery?.provinceCode) {
      bindProvinceCode(this.ProvinceOceanCodes, delivery);
    }
    if (!this.taxesStateData.includes(delivery.provinceCode?.toUpperCase())) return; // 免税州
    // 完全成功标记 || 已经校验过 不重新请求
    if (
      delivery?.resolveAddress ||
      (delivery?.delivery_tax && typeof delivery.delivery_tax?.validateAddress === 'boolean')
    ) {
      this._orderCount.salesTax = delivery.delivery_tax?.salesTax ?? 0;
      return;
    }

    if (code === 'US') {
      return this.getAddressTaxValidate_US(delivery);
    } else if (code === 'CA') {
      return this.getAddressTaxValidate_CA(delivery);
    }
    return null;
  };

  /**
   * 校验地址是否符合税率计算要求，具体参数参考getAddressTaxValidate
   */
  getAddressTaxValidate_US = (delivery) => {
    this.confirmFormEditing._deliveryValidating = true;
    PaymentTracker.orderSaleTaxTracker({ action: 'salesTax_address_validate' });

    return httpRequest({
      url: SERVER.API_SERVER_USER + '/delivery/resolveAddress',
      method: 'post',
      data: getDeliveryObj(delivery, 'reqToTax'),
    })
      .then((res) => {
        // 完全失败
        if (res.code === 23007) {
          delivery.delivery_tax = { validateAddress: false };
          delivery.resolveAddress = false;
          PaymentTracker.orderSaleTaxTracker({ action: 'salesTax_address_validate_error' });
        } else if (res.code !== 0) {
          throw Error('getAddressTaxValidate_US system error');
        }

        if (res.code === 0) {
          const newAddress = res.data?.resolveAddress?.provinceCode
            ? getDeliveryObj(res.data?.resolveAddress, 'resToInit', {
                ProvinceOceanCodes: this.ProvinceOceanCodes,
              }) || {}
            : {};

          // 处理比对新旧地址的state、city、zipcode
          const zipCodeLength = `${delivery.zip_code}`.trim()?.length;
          newAddress.zip_code = `${newAddress.zip_code}`.substring(0, zipCodeLength);
          const isSameDelivery =
            !isEmpty(newAddress) &&
            isSameStr(newAddress.provinceCode, delivery.provinceCode, 'equal') &&
            isSameStr(newAddress.city, delivery.city, 'equal') &&
            isSameStr(newAddress.zip_code, `${delivery.zip_code}`.substring(0, zipCodeLength), 'equal');

          if (isSameDelivery) {
            delivery.resolveAddress = true;
            this.confirmFormEditing._saleTaxFetchLoading = true; // 完全成功了肯定要查税率
            PaymentTracker.orderSaleTaxTracker({ action: 'salesTax_address_validate_success' });
          } else {
            delivery.resolveAddress = false;
          }
          delivery.delivery_tax = {
            confirmDelivery: newAddress,
            isConfirmDelivery: false,
            salesTax: 0,
            validateAddress: true,
            ...(delivery?.delivery_tax ?? {}),
          };
        }

        this.confirmFormEditing._deliveryValidating = false;
        return delivery;
      })
      .catch((e) => {
        // 其他错误视为校验通过
        console.error(e);
        // 调用错误
        delivery.delivery_tax = { validateAddressError: true };
        PaymentTracker.orderSaleTaxTracker({ action: 'salesTax_address_validate_error' });
        this.confirmFormEditing._deliveryValidating = false;
        toggleGlobalScroll(false);
        return delivery;
      });
  };

  /**
   * 校验地址是否符合税率计算要求，具体参数参考getAddressTaxValidate
   * 加拿大的地址校验不用调接口，只需要判断省地址是否正确
   */
  getAddressTaxValidate_CA = (delivery) => {
    const {
      country: { code },
    } = this.root.area;
    if (code !== 'CA') return;
    // 已经判断过地址校验的不动
    if (typeof delivery.delivery_tax === 'object') return;

    if (!delivery.provinceCode) {
      bindProvinceCode(this.ProvinceOceanCodes, delivery);
    }
    if (delivery.provinceCode && !this.taxesStateData.includes(delivery.provinceCode?.toUpperCase())) return; // 免税州

    if (delivery.resolveAddress) {
      delivery.delivery_tax = {
        salesTax: 0,
        validateAddress: true,
      };
      return delivery;
    } else {
      delivery.resolveAddress = !delivery.needUpdateProvince;
      delivery.delivery_tax = {
        salesTax: 0,
        validateAddress: delivery.resolveAddress ? true : !delivery.needUpdateProvince,
      };
      if (delivery.resolveAddress) {
        PaymentTracker.orderSaleTaxTracker({ action: 'salesTax_address_validate_success' });
      } else {
        PaymentTracker.orderSaleTaxTracker({ action: 'salesTax_address_validate_error' });
      }
      return delivery;
    }
  };

  /**
   * 查询消费税税率
   * @param action init 默认地址将直接认为地址校验完成
   * payment支付 / coupon优惠券 时调用， 此时切换地址将重新计算税率
   */
  @action
  getConsumptionTax = (_delivery, action) => {
    const { orderCartItems, _deliveryDetail, _deliveries, _orderCount, _payment } = this;
    const {
      country: { code },
    } = this.root.area;
    const { email, guest } = this.root.auth;
    const delivery = _delivery || _deliveryDetail;

    // 结账页和二次支付页的数据不同，贸然赋值可能出现bug
    let payload = {};
    _orderCount.salesTax = 0;
    if (!['US', 'CA'].includes(code)) return;
    if (_orderCount.isFreeOrder) return;
    if (isEmpty(orderCartItems)) return;

    if (action && action !== 'init') {
      _deliveries.forEach((d) => {
        if (d.delivery_tax) {
          d.delivery_tax.canReCheckTax = true;
        }
      });
      if (delivery.delivery_tax) {
        delivery.delivery_tax.canReCheckTax = true;
      }
    }
    if (!delivery) return;
    if (!delivery.provinceCode) {
      bindProvinceCode(this.ProvinceOceanCodes, delivery);
    }
    if (!this.taxesStateData.includes(delivery.provinceCode?.toUpperCase?.())) return; // 免税州
    // 参数：地址、总金额、商品(没有赠品)

    // 已经请求过，而且没有修改支付方式 / 优惠券 不重新请求
    if (delivery.delivery_tax && delivery.delivery_tax.canReCheckTax === false) {
      this._orderCount.salesTax = delivery.delivery_tax?.salesTax ?? 0;
      return;
    }

    const cartItems = orderCartItems;

    payload = {
      uniqueKey: delivery.delivery_tax?.uniqueKey,
      ...getDeliveryObj(delivery, 'reqToTax'),
      contactEmail: email || delivery.email || guest,
      resellerCode: this.root.app.insrc,
      'tradeCode': _orderCount.coupon,
      'prePaymentChannel': _payment.type === 'paypal' ? 'paypal_ss' : _payment.type,
      'itemArrays': cartItems.map((d) => ({
        pid: d.productId,
        id: d.commodityId,
        number: d.count,
        bind_services:
          d.bind_services?.map((s) => ({
            id: s.id,
            number: d.count,
            type: s.serviceType,
          })) ?? undefined,
      })),
    };

    payload.itemArrays = this.handleCareServiceForUS({ type: 'filterCare', items: payload.itemArrays });

    this.confirmFormEditing._saleTaxFetchLoading = true;
    PaymentTracker.orderSaleTaxTracker({ action: 'salesTax_validate' });
    return httpRequest({
      url: SERVER.API_SERVER_TRADE + '/tax/calculateTax',
      method: 'post',
      data: payload,
      instaToken: true,
    })
      .then((res) => {
        // 失败场景 (地址完全失败错误码同orderCreate中的税率错误场景)
        if (res.code !== 0) {
          if (res.code === -1) {
            throw Error('system error');
          } else {
            delivery.delivery_tax = {
              ...delivery.delivery_tax,
              validateAddress: false,
              validateTax: false,
              canReCheckTax: false,
            };
            PaymentTracker.orderSaleTaxTracker({ action: 'salesTax_validate_error' });
          }
        }
        if (res.code === 0 && res.data) {
          const { totalTax, uniqueKey } = res.data?.taxResult || {};
          if (this._deliveryDetail?.id === delivery.id) {
            // loading中切换地址
            _orderCount.salesTax = totalTax;
          }
          delivery.delivery_tax = {
            ...delivery.delivery_tax,
            salesTax: totalTax,
            validateTax: true,
            canReCheckTax: false,
          };
          // 后端缓存key对应的是没有优惠的基础地址+商品金额，如果存在支付优惠则缓存key返回为空，此时不做任何处理，无需记住为空的缓存key。
          if (uniqueKey) {
            delivery.delivery_tax.uniqueKey = uniqueKey;
          }

          if (action === 'init' && typeof delivery.delivery_tax.validateAddress !== 'boolean') {
            delivery.delivery_tax.validateAddress = true;
            delivery.delivery_tax.isConfirmDelivery = true;
          }

          PaymentTracker.orderSaleTaxTracker({ action: 'salesTax_validate_success' });
        }
        this.confirmFormEditing._saleTaxFetchLoading = false;
        return delivery;
      })
      .catch((e) => {
        armsErrorSend(e, { filename: 'getConsumptionTax error' });
        // 调用错误
        PaymentTracker.orderSaleTaxTracker({ action: 'salesTax_validate_error' });
        delivery.delivery_tax = { validateTax: false, validateTaxError: true };
        this.confirmFormEditing._saleTaxFetchLoading = false;
        return delivery;
      });
  };

  /** 二次支付查询消费税税额 */
  @action
  getRepayConsumptionTax = (payload) => {
    PaymentTracker.orderSaleTaxTracker({ action: 'repay_salesTax_validate' });
    return httpRequest({
      url: SERVER.API_SERVER_TRADE + '/tax/orderPayedAgainCalculateTax',
      method: 'post',
      data: payload,
    })
      .then((res) => {
        // 失败场景 (地址完全失败错误码同orderCreate中的税率错误场景)
        if (res.code !== 0) {
          if (res.code === -1) {
            throw Error('system error');
          } else {
            payload.delivery_tax = { validateAddress: false, validateTax: false };
            PaymentTracker.orderSaleTaxTracker({ action: 'repay_salesTax_validate_error' });
          }
        }
        if (res.code === 0) {
          const { totalTax, uniqueKey } = res.data?.taxResult || {};
          payload.delivery_tax = {
            salesTax: totalTax,
            uniqueKey: uniqueKey,
            validateTax: true,
          };

          PaymentTracker.orderSaleTaxTracker({ action: 'repay_salesTax_validate_success' });
        }

        return payload;
      })
      .catch((e) => {
        armsErrorSend(e, { filename: 'getRepayConsumptionTax error' });
        // 调用错误
        PaymentTracker.orderSaleTaxTracker({ action: 'repay_salesTax_validate_error' });
        payload.delivery_tax = { validateTax: false, validateTaxError: true };
        return payload;
      });
  };

  /** 获取收税州 */
  getTaxesStateData = (area, key = 'provinceCode') => {
    const {
      country: { code },
    } = this.root.area;
    area = area || code;
    if (!['US', 'CA'].includes(area)) return;
    if (!isEmpty(this.taxesStateData)) return;

    httpRequest({
      url: SERVER.API_SERVER_META + `/tax/province/listAvalaraTaxProvince?countryCode=${area}`,
    }).then((res) => {
      if (res.code === 0) {
        this.taxesStateData = res.data?.avalaraTaxProvince?.map((d) => d?.[key]?.toUpperCase()) ?? [];
      }
    });
  };

  /**
   * 修改选择的快递地址
   * */
  @action
  setDeliveryDetail = (deliveryDetail) => {
    const { _deliveries } = this;
    if (!isEmpty(deliveryDetail)) {
      this.setStateValue('_deliveryDetail', deliveryDetail);
      this._deliveries = _deliveries?.map((d) => (d.id === deliveryDetail.id ? deliveryDetail : d)) ?? [];
    } else if (_deliveries && _deliveries.length > 0) {
      const _deliveryDetail = _deliveries.filter((o) => o.is_default)[0] ?? _deliveries[0];
      this.setStateValue('_deliveryDetail', _deliveryDetail);
    } else {
      this.setStateValue('_deliveryDetail', {});
    }
  };

  @action
  listDelivery = () => {
    const {
      country: { code: CountryCode },
    } = this.root.area;
    return httpRequest({
      url: SERVER.API_SERVER_USER + '/delivery/list',
      instaToken: true,
    }).then(async ({ code, data }) => {
      if (code === 0) {
        if (CountryCode !== 'CN') {
          // 非香港、澳门、新加坡province要走接口取数据，下拉选择
          if (!['SG', 'HK', 'MO', 'TW'].includes(CountryCode)) {
            const OceanCodesData = await this.getProvinceOceanCodes();
            if (OceanCodesData && OceanCodesData.length > 0) {
              const temp = JSON.parse(JSON.stringify(data.deliveries));

              data.deliveries = data.deliveries.map((o) => {
                try {
                  o.advice_province = o.advice_province || OceanCodesData[0]['province_name'];
                  const UserProvinceLowerCase = o.province.toLowerCase();
                  const AdviceProvinceLowerCase = o.advice_province.toLowerCase();
                  const matchExact = UserProvinceLowerCase === AdviceProvinceLowerCase;
                  const matchOceanCode = OceanCodesData.filter((item) => item.province_name === o.advice_province)[0];
                  if (!matchOceanCode) return null;
                  if (matchExact) {
                    o.needUpdateProvince = false;
                    o.province = matchOceanCode.province_name;
                    o.ocean_code = matchOceanCode.ocean_code;
                  } else {
                    o.needUpdateProvince = true;
                    o.province = '';
                    o.provinceFuzzy = matchOceanCode.province_name;
                    o.ocean_code = matchOceanCode.ocean_code;
                  }

                  if (CountryCode === 'US') {
                    // 匹配之前地址的税率查询信息
                    o.delivery_tax = this._deliveries?.find((d) => d.id === o.id)?.delivery_tax;
                    o.needUpdateZipCode = !validator({ type: 'zipCode', val: o.zip_code, extra: { CountryCode } }); // 检查美国地区zipcode是否合法
                  }
                  if (CountryCode === 'CA') {
                    // 匹配之前地址的税率查询信息
                    const _delivery = this._deliveries?.find((d) => d.id === o.id);
                    o.delivery_tax = _delivery?.delivery_tax;
                    o.resolveAddress = _delivery?.resolveAddress;
                    const _d = this.getAddressTaxValidate_CA(o);
                    o = _d ?? o;
                  }
                  return o;
                } catch (e) {
                  return this.root.auth.dataCollect({
                    event: 'deliveryListErr',
                    data: {
                      data: temp,
                      e: e + '',
                    },
                  });
                }
              });
            }
          }
          this._deliveries = data.deliveries.filter((o) => !!o);
          this.setDeliveryDetail();
        }
        if (CountryCode === 'CN') {
          data.deliveries.forEach(async (o) => {
            // 中国大陆地址sub_address字段移除，仅保留address，需把历史数据回显至address
            o.address = `${o.address} ${o.sub_address ?? ''}`;
            o.sub_address = '';
          });
          this.setStateValue('_deliveries', data.deliveries);
          this.setDeliveryDetail();
        }
        return data.deliveries;
      }
    });
  };

  @action
  handleDeliveryRegions = async (delivery) => {
    const {
      fetchProvinceCity,
      country: { code },
    } = this.root.area;
    if (code !== 'CN') return null;
    const matchRegions = {
      // 城市没有匹配上, 提示需要用户点击修改按钮，更新城市和区县
      needUpdateRegions: false,
      // 城市匹配上了，区县没有匹配上，提示需要用户主动选择区县，更新区县（不阻断用户下单）
      needUpdateDistrict: false,
      // 省份、城市、区县均匹配上了，但是需要点击用户确认(执行update)/修改（不阻断用户下单）
      needUpdateMatchRegions: false,
      // 匹配的省份
      province: '',
      // 匹配的省份数据
      provinceData: null,
      // 匹配的城市（可能为空）
      city: '',
      // 匹配的城市数据
      cityData: null,
      // 当前匹配到城市的列表数据(用于编辑选择)
      cityList: [],
      // 匹配的区县（可能为空）
      district: '',
      // 匹配的区县数据
      districtData: null,
      // 当前匹配到区县的列表数据(用于编辑选择)
      districtList: [],
    };

    try {
      const { provinceMap } = require('@/components/ChinaRegionsPicker/regionsData');
      matchRegions.provinceData = provinceMap
        .map((o) => o.list)
        .flat()
        .filter((o) => delivery.province && o.name.includes(delivery.province))[0];
      matchRegions.province = matchRegions.provinceData.name;
      matchRegions.cityList = await fetchProvinceCity(matchRegions.provinceData.AdCode);

      // 直辖市（注：直辖市city字段历史数据存的是区县）
      if (matchRegions.provinceData.municipality) {
        // 直辖市province值赋给city
        matchRegions.city = matchRegions.provinceData.name;
        matchRegions.districtList = matchRegions.cityList[0].children;
        // 历史数据，不存在区县
        if (!delivery.district) {
          // 用city匹配区县
          matchRegions.districtData = matchRegions.districtList.filter(
            (o) => delivery.city && o.name.includes(delivery.city)
          )[0];
          if (matchRegions.districtData) {
            matchRegions.district = matchRegions.districtData.name;
          } else {
            matchRegions.districtData = matchRegions.districtList.filter((o) =>
              delivery.address.includes(o.name.replace(/区|县|市|旗|城|会|岛|域/g, ''))
            )[0];
            if (matchRegions.districtData) {
              matchRegions.district = matchRegions.districtData.name;
            } else {
              matchRegions.needUpdateDistrict = true;
            }
          }
        } else {
          // 非历史数据
          matchRegions.districtData = matchRegions.districtList.filter(
            (o) => delivery.district && o.name.includes(delivery.district)
          )[0];
          if (matchRegions.districtData) {
            matchRegions.district = matchRegions.districtData.name;
          } else {
            matchRegions.needUpdateDistrict = true;
          }
        }
      }
      // 非直辖市，分为省市区三级和省市二级（地级市城市，例如中山、东莞）
      if (!matchRegions.provinceData.municipality) {
        matchRegions.cityData = matchRegions.cityList.filter((o) => delivery.city && o.name.includes(delivery.city))[0];
        if (matchRegions.cityData) {
          // 城市匹配上，把城市名赋给city
          matchRegions.city = matchRegions.cityData.name;
          matchRegions.districtList = matchRegions.cityData.children;
          // 非地级市存在区县
          if (matchRegions.districtList.length) {
            if (delivery.district) {
              // 地址存在district，则匹配数据是否存在区县列表
              matchRegions.districtData = matchRegions.districtList.filter(
                (o) => delivery.district && o.name.includes(delivery.district)
              )[0];
            } else {
              // 地址列表不存在district为历史数据，尝试通过address从区县列表匹配
              matchRegions.districtData = matchRegions.districtList.filter((o) =>
                delivery.address.includes(o.name.replace(/区|县|市|旗|城|会|岛|域/g, ''))
              )[0];
            }
            if (matchRegions.districtData) {
              matchRegions.district = matchRegions.districtData.name;
            } else {
              matchRegions.needUpdateDistrict = true;
            }
          }
        } else {
          // 城市没有匹配上则需要手动点击编辑更新
          matchRegions.needUpdateRegions = true;
        }
      }

      // 如果区县为空，则存在区县列表，则补位第一个
      if (matchRegions.needUpdateDistrict && !matchRegions.district && matchRegions.districtList.length) {
        matchRegions.district = matchRegions.districtList[0]['name'];
      }
    } catch (e) {
      // 其他任何异常都需要用户更新地址（阻断下单）
      // e.g. 省份未匹配上
      matchRegions.needUpdateRegions = true;
    }

    matchRegions.needUpdateMatchRegions =
      matchRegions.province !== delivery.province ||
      matchRegions.city !== delivery.city ||
      matchRegions.district !== delivery.district;

    return matchRegions;
  };

  @action
  addDelivery = (delivery, cb) => {
    this.root.app.setGlobalValue('G_GlobalLoading', true);
    const url = SERVER.API_SERVER_USER + '/delivery/addDelivery';

    const data = bindProvinceCode(this.ProvinceOceanCodes, getDeliveryObj(delivery));

    httpRequest({
      url,
      method: 'post',
      data,
      instaToken: true,
    })
      .then((res) => {
        this.root.app.setGlobalValue('G_GlobalLoading', false);
        if (res.code !== 0) {
          message.warning(res?.msg ?? res);
        }
        if (res.code === 0) {
          // 存储返回的id和新增的地址信息，后续本地可以将税率比对上，设置_deliveryDetail用于新增地址的税率地址校验
          const deliveryHasId = { ...delivery, ...(res.data?.delivery || {}) };
          this._deliveries.unshift(deliveryHasId);
          this.setStateValue('_deliveryDetail', deliveryHasId);
          this.listDelivery();
          cb && cb(deliveryHasId);
        }
      })
      .catch((err) => {
        this.root.app.setGlobalValue('G_GlobalLoading', false);
        message.warning(err);
      });
  };

  /**
   * @param {*} type 'update'默认更新全部list，type='single'只更新不请求全部
   */
  @action
  updateDelivery = (delivery, cb, type = 'update') => {
    type !== 'single' && this.root.app.setGlobalValue('G_GlobalLoading', true);
    const url = SERVER.API_SERVER_USER + '/delivery/updateDelivery';
    const data = bindProvinceCode(this.ProvinceOceanCodes, getDeliveryObj(delivery));
    httpRequest({
      url,
      method: 'post',
      data,
      instaToken: true,
    })
      .then((res) => {
        this.root.app.setGlobalValue('G_GlobalLoading', false);
        if (res.code === 0) {
          this.setDeliveryDetail(delivery);
          type !== 'single' && this.listDelivery();
          cb && cb(delivery);
        } else {
          message.warning(`${res.code} ${res.msg ?? ''}`);
        }
      })
      .catch((err) => {
        this.root.app.setGlobalValue('G_GlobalLoading', false);
        message.warning(err);
      });
  };

  @action
  delDelivery = (deliveryId, cb) => {
    this.root.app.setGlobalValue('G_GlobalLoading', true);
    const url = SERVER.API_SERVER_USER + '/delivery/delete';
    httpRequest({
      url,
      params: {
        id: deliveryId,
      },
      instaToken: true,
    })
      .then((res) => {
        this.root.app.setGlobalValue('G_GlobalLoading', false);
        if (res.code === 0) {
          this.listDelivery();
          cb && cb();
        }
      })
      .catch((err) => {
        this.root.app.setGlobalValue('G_GlobalLoading', false);
        message.warning(err);
      });
  };

  /** flag = true 新加入的产品插入到数组的第一位 */
  /**
   * @description 加入单个套餐到购物车
   * @param {Object} data 套餐信息
   * @param {String} type 是否直接加入购物车 once | not-once
   * @param {Function} cb 回调
   * @param {Boolean} flag 是否将新加入的产品插入第一位
   * @param {Boolean | Object} ecommerceTracker 是否在方法内上报GA电子商务事件,可传对象透传参数至电子商务事件
   * @param {String} from 加购的来源 productPage=产品页 ||
   *
   */
  @action
  addToCart = (data, type = 'not-once', cb, flag = true, ecommerceTracker = {}, from) => {
    const newItem = {
      name: data.product.info.name,
      info: data.product.info,
      key: data.commodity.id,
      thumb: data.commodity.displays[0].url_m,
      product_name: data.product.product_name,
      count: data.count,
      price: data.commodity.price,
      origin_price: data.commodity.origin_price,
      reseller_discount_price: data.commodity.reseller_discount_price || {},
      commodity: data.commodity,
      delivery_time_config: data.commodity.delivery_time_config || data.product.delivery_time_config || null,
      productId: data.product.id,
      commodityId: data.commodity.id,
      bind_services: data.bind_services || [],
      bindClimbServices: data.bindClimbServices ?? [],
      bind_node: data.bind_node || null,
      selected: true,
    };

    try {
      if (ecommerceTracker) {
        const listName = typeof ecommerceTracker === 'object' ? ecommerceTracker.listName : '';
        EcommerceTracker.addToCart({ item: newItem, listName });
      }
    } catch (err) {
      console.error(err);
    }

    this.root.app.trackFb('AddToCart', newItem);
    this.root.app.trackTiktok('AddToCart', newItem);
    this.root.app.trackTwitter('AddToCart', { data: newItem });
    EcommerceTracker.trackTTD('addToCart');

    // PushEngage开始推送通知
    ThirdPartyTracker.PushEngageTracker.addToCart({ language: this.root.area.country?.X_Language });

    // 非产品页加购的场景，移除非当前SKU删除T恤赠品的记录，产品页逻辑单独处理
    if (from !== 'productPage') {
      const { GiftTShirt } = this.root.app.campaignWrap.campaignWrap;
      GiftTShirt.restoreCurSkuSizeCache({ curSelectCommodity: data?.commodity?.id });
    }

    if (type === 'once') {
      this.setOrderCartItems([newItem]);
    } else {
      let itemToAdd = newItem;
      let itemToAddExistIndex = -1; // 已存在的商品排序
      let added = true;
      const { order_buy_limit, stock } = newItem.commodity;
      const max = order_buy_limit ? (order_buy_limit < stock ? order_buy_limit : stock) : stock;

      // 新加入云存储商品，删除之前的
      if (this.hasCloudServiceProduct && hasCloudService(newItem)) {
        const index = this.allCartItems.findIndex((d) => hasCloudService(d));
        this.allCartItems.splice(index, 1);
      }

      let itemsAfterAdd = this.allCartItems.map((item, index) => {
        if (item.commodityId === itemToAdd.commodityId) {
          item.count += itemToAdd.count;
          if (item.count >= max) {
            item.count = max;
            added = false;
          }
          if (itemToAdd.bind_node && itemToAdd.bind_node.timeStamp) {
            item.bind_node = itemToAdd.bind_node;
          }

          itemToAddExistIndex = index;
        }
        return item;
      });

      // 修改最新加入的商品为首位
      if (itemToAddExistIndex > 0 && flag) {
        const item = itemsAfterAdd.splice(itemToAddExistIndex, 1)[0];
        itemsAfterAdd.unshift(item);
      }

      if (itemToAddExistIndex < 0) {
        if (itemsAfterAdd.bind_node && itemsAfterAdd.bind_node.timeStamp) {
          itemsAfterAdd.unshift(itemToAdd);
        } else {
          flag ? itemsAfterAdd.unshift(itemToAdd) : itemsAfterAdd.push(itemToAdd);
        }
      }

      this.setCartItems(itemsAfterAdd, true);
      cb && cb(added, itemsAfterAdd);
    }
  };

  /**
   * 产品卡片加购
   * @param {AddToCartType} addToCartType 加购类型
   * @param {{
   *   productKey: string,
   *   pid: string,
   *   cid: string,
   *   productName: string,
   *   commodityName: string,
   *   commodityDisplayImage: string,
   *   price: number,
   *   originPrice: number?,
   *   stockCount: number?,
   *   orderBuyLimit: number?,
   * }} commodity 商品信息
   * @param {Object} rest addToCart的其他参数
   */
  @action
  productCardAddToCart = (addToCartType, commodity, ...rest) => {
    const {
      productKey,
      pid,
      cid,
      productName,
      commodityName,
      commodityDisplayImage,
      price,
      originPrice,
      stockCount,
      orderBuyLimit,
    } = commodity;
    // 8K、增值服务、第三方导流、定制产品、需要隐藏价格的产品，点击跳转产品页
    if (
      [
        AddToCartType.EIGHT_K,
        AddToCartType.EXTRA_SERVICE,
        AddToCartType.THIRD_PARTY,
        AddToCartType.CUSTOM_PRODUCT,
        AddToCartType.NEED_HIDE_PRICE,
      ].includes(addToCartType)
    ) {
      return browserPushUrl(`/product/${productKey}`);
    }

    // 缺货/库存不足的产品，显示产品订阅
    if (AddToCartType.OUT_OF_STOCK === addToCartType) {
      this.root.product.setStateValue('subscribeCommodity', {
        commodityId: cid,
        productId: pid,
      });
      this.root.product.setStateValue('subscribeCommodityModalVisible', true);
      return;
    }

    const item = {
      product: {
        id: pid,
        info: {
          name: productName,
        },
      },
      commodity: {
        id: cid,
        displays: [
          {
            url_m: commodityDisplayImage,
          },
        ],
        info: {
          name: commodityName,
        },
        price: price,
        origin_price: originPrice,
        stock: stockCount || 10,
        order_buy_limit: orderBuyLimit || 1,
      },
      count: 1,
    };
    this.addToCart(item, 'not-once', ...rest);
  };

  @action
  removeFromCart = (item, cb) => {
    if (!item) return;
    let itemToRemove = item;
    let itemsAfterRemove = this.allCartItems.filter((item) => {
      return item.commodityId !== itemToRemove.commodityId;
    });

    this.setCartItems(itemsAfterRemove, true);
    cb && cb(itemsAfterRemove);

    return itemsAfterRemove;
  };

  @action
  removeServiceFromCart = (params) => {
    const { item, service, cb } = params;
    const itemsAfterRemove = this.allCartItems;
    itemsAfterRemove.forEach((o) => {
      if (o.commodityId === item.commodityId) {
        o.bind_services = o.bind_services.filter((s) => s.id !== service.id);
      }
    });

    this.setCartItems(itemsAfterRemove, true);
    cb && cb(itemsAfterRemove);

    return this.getCartItemsDetail();
  };

  @action
  plusCartItem = (item, cb) => {
    let itemToPlus = item;

    let itemsAfterPlus = cloneDeep(this.allCartItems).map((item) => {
      if (item.commodityId === itemToPlus.commodityId) {
        item.count++;
      }
      return item;
    });

    this.setCartItems(itemsAfterPlus, true);
    cb && cb(itemsAfterPlus);

    return itemsAfterPlus;
  };

  @action
  minusCartItem = (item, cb) => {
    let itemToMinus = item;
    let itemsAfterMinus = cloneDeep(this.allCartItems).map((item) => {
      if (item.commodityId === itemToMinus.commodityId) {
        item.count--;
        item.count = item.count < 1 ? 1 : item.count;
      }
      return item;
    });

    this.setCartItems(itemsAfterMinus, true);
    cb && cb(itemsAfterMinus);

    return itemsAfterMinus;
  };

  // 通过填空窗口变更item数量 (勾选变更请通过changeCartItemSelected)
  @action
  changeCartItem = (item, cb) => {
    let itemToChange = item;
    let itemsAfterChange = cloneDeep(this.allCartItems).map((item) => {
      if (item.commodityId === itemToChange.commodityId) {
        return itemToChange;
      } else {
        return item;
      }
    });

    this.setCartItems(itemsAfterChange, true);
    cb && cb(itemsAfterChange);

    return itemsAfterChange;
  };

  /**
   * 用户登出后清空本地购物车
   */
  @action
  clearCart = () => {
    this.setCartItems([]);
  };

  @action
  resetGifts = () => {
    this.gifts = [];
    this.orderConfirmGifts = [];
  };

  @action
  getOrderHasReportedState = (orderNumber) => {
    const url = SERVER.API_SERVER_META + '/sc/getOrderState';
    return httpRequest({
      url,
      params: {
        orderNumber,
      },
    });
  };

  @action
  setOrderHasReportedState = (data) => {
    // data: { orderNumber, email }
    const url = SERVER.API_SERVER_META + '/sc/setOrderState';
    return httpRequest({
      url,
      method: 'post',
      data,
    });
  };

  @action
  getSimpleOrderInfo = (params = {}) => {
    const { orderNumber, opts, callback } = params;
    const url = SERVER.API_SERVER_TRADE + '/order/getSimpleOrderInfo';
    return httpRequest({
      url,
      X_Language: opts?.X_Language,
      params: {
        orderNumber,
        checkNumber: encryptBE(orderNumber),
      },
    })
      .then((res) => {
        if (res.code === 0 && res.data) {
          // 二次支付中可能存在切换税费 / 总金额的场景，存储原本的数据
          res.data.simpleOrderInfo.originPaymentInfo = { ...(res.data.simpleOrderInfo?.orderPaymentInfo || {}) };
          this.simpleDetailOrder = res.data.simpleOrderInfo || {};
          // id和order_number的设置是为了支付校验 / 二次支付
          this.detailOrder.id = res.data?.simpleOrderInfo.orderId;
          this.detailOrder.order_number = res.data.simpleOrderInfo.orderNumber;
          this.detailOrder.orderNumber = res.data.simpleOrderInfo.orderNumber;
          this.detailOrder.area = res.data.simpleOrderInfo.area;
        }
        callback && callback(res);
        return res;
      })
      .catch(() => {
        callback && callback();
      });
  };

  @action
  getOrderInfo = (params = {}) => {
    const { orderNumber, guestToken, callback, opts = {} } = params;
    const url = SERVER.API_SERVER_TRADE + '/order/getOrderInfo';
    httpRequest({
      url,
      params: {
        order_number: orderNumber,
        check_number: guestToken ? null : encryptBE(orderNumber + ''),
        guest_token: guestToken || null,
      },
      X_Language: opts.X_Language || '',
      instaToken: true,
    }).then((res) => {
      if (res.code === 0) {
        if (opts.from !== 'DataLayer') {
          res.data.order.items.forEach((o) => {
            o.commodityId = o.commodity;
            o.productId = o.product;
          });
          this.detailOrder = res.data.order;
        }
      } else {
        console.error(res.code + 'n error');
      }
      callback && callback(res);
    });
  };

  @action
  getTrackOrderInfo = (params) => {
    const { order_number, email, callback } = params;
    this.root.app.setGlobalValue('G_GlobalLoading', true);
    const url = SERVER.API_SERVER_TRADE + '/order/getTrackOrderInfo';
    httpRequest({
      url,
      params: {
        order_number,
        email,
      },
    }).then((res) => {
      const { LANG } = this.root.area;
      this.root.app.setGlobalValue('G_GlobalLoading', false);
      if (res.code === 0) {
        res.data.order.items.forEach((o) => {
          o.commodityId = o.commodity;
        });
        this.detailOrder = res.data.order;
        callback && callback();
      }
      if (res.code === 3) {
        message.warning(LANG['common.tips.orderNumber_email_notMatch']);
      }
      if (res.code === 21200) {
        message.warning(LANG['common.tips.order_notFound']);
      }
    });
  };

  @action
  getOrderDeliveryInfo = (params) => {
    const { order_number, callback } = params;
    const url = SERVER.API_SERVER_ORDER + '/delivery/getOrderDeliveryInfo';
    httpRequest({
      url,
      instaToken: true,
      params: {
        order_number,
      },
    }).then((res) => {
      const { LANG } = this.root.area;
      if (res.code === 21200) {
        message.warning(LANG['common.tips.order_notFound']);
      } else {
        callback && callback(res);
      }
    });
  };

  @action
  getUserOrders = (page, cb) => {
    const url = SERVER.API_SERVER_TRADE + '/order/getUserOrders';
    httpRequest({
      url,
      params: page,
      instaToken: true,
    }).then(({ code, data }) => {
      if (code === 0) {
        cb && cb(data);
      }
    });
  };

  /**
   *
   * @description 获取运费差额（用于购物车显示），税费，（运费迁移新接口：/calculateShippingFee）
   * @param {Object} data 计算额外费用相关的item列表
   */
  @action
  fetchOrderExtraFee = (data, cb) => {
    if (data.items.length === 0) {
      isCalculating = false;
      return;
    }
    httpRequest({
      url: SERVER.API_SERVER_TRADE + '/order/getTradeData',
      method: 'post',
      data,
    })
      .then((res) => {
        if (res.code === 0) {
          cb?.(this.cartExtraFee?.freeDifference?.amount, res.data.freeDifference?.amount);
          this.cartExtraFee = { ...this.cartExtraFee, ...res.data };
        } else {
          this.cartExtraFee = {};
          armsErrorSend('接口异常 getTradeData', { filename: 'Mobx Order', data: { data, res } });
        }
      })
      .finally(() => {
        isCalculating = false;
      });
  };

  /**
   * 根据前端缓存的包邮门槛判断订单是否有运费
   * 达到包邮门槛，则设置shippingFee.amount为0，UI根据这个显示Free
   * 未达到包邮门槛，调用calculateShippingFee计算运费
   */
  @action
  calcShippingFeeIfRequired = (items, calculateShippingFeeCallback) => {
    const {
      country: { currency, signal },
    } = this.root.area;
    const { isCartPage } = this.root.app;
    const itemPriceAmount = isCartPage ? this.cartTotalPrice.itemsAmount : 0;
    const freeShippingAmount = this.root.meta.freeShippingAmount?.shippingCost?.freeLimit;
    const itemLength = items.length;
    const setZeroShippingFee = () => {
      this.cartExtraFee = {
        ...this.cartExtraFee,
        shippingFee: {
          // 显示Free
          amount: 0,
          currency,
          signal,
        },
      };
      isCalculating = false;
    };
    if (itemLength === 0) {
      setZeroShippingFee();
      return;
    }
    if (itemPriceAmount > freeShippingAmount) {
      //  从【有运费 -> 包邮】的状态变换Case，上报GA
      if (this.cartExtraFee?.shippingFee?.amount !== 0) {
        CartTracker.cartPage({
          action: 'cart_shipping_cost',
          label: 'Free',
        });
      }
      setZeroShippingFee();
    } else this.calculateShippingFee({ items }, calculateShippingFeeCallback);
  };

  /**
   * 获取新版运费（只需要在结账页计算）
   * @param {Object} data 计算运费的item列表
   */
  @action
  calculateShippingFee = (data, cb) => {
    if (data.items.length === 0) {
      isCalculating = false;
      return;
    }
    httpRequest({
      url: SERVER.API_SERVER_TRADE + '/shipping/calculateShippingFee',
      method: 'post',
      data: data.items,
    })
      .then((res) => {
        if (res.code === 0) {
          const { shippingFee } = res.data;
          cb?.(this.cartExtraFee?.shippingFee?.amount, shippingFee);
          this.cartExtraFee = { ...this.cartExtraFee, shippingFee };
        } else {
          this.cartExtraFee = {};
          armsErrorSend('接口异常 calculateShippingFee', { filename: 'Mobx Order', data: { data, res } });
        }
      })
      .finally(() => {
        isCalculating = false;
      });
  };

  @action
  confirmDelivery = (orderNumber) => {
    const url = SERVER.API_SERVER_TRADE + '/order/confirmDelivery';
    httpRequest({
      url,
      method: 'post',
      data: {
        orderNumber,
      },
      instaToken: true,
    }).then((res) => {
      if (res.code === 0) {
        window.location.href = handleQueryParams('t', new Date().getTime());
      }
    });
  };

  @action
  cancelOrder = (data, cb) => {
    const url = SERVER.API_SERVER_TRADE + '/order/cancel';
    httpRequest({
      url,
      method: 'post',
      data,
      instaToken: true,
    })
      .then((res) => {
        if (res.code === 0) {
          cb && cb();
        }
      })
      .catch(() => {
        window.location.href = handleQueryParams('t', new Date().getTime());
      });
  };

  /**
   * 存在支付优惠时调用，从后端获取支付优惠的折扣金额，避免前后端计算折扣公式不同造成的金额误差
   * 使用前需判断是否存在支付优惠，方法中不判断。
   */
  getPaymentDiscount = () => {
    const { email, guest } = this.root.auth;
    const { _orderCount, _payment, orderCartItems, simpleDetailOrder = {} } = this;

    const data = isEmpty(simpleDetailOrder)
      ? {
          contactEmail: email || guest,
          'tradeCode': _orderCount.coupon,
          'prePaymentChannel': _payment.type === 'paypal' ? 'paypal_ss' : _payment.type,
          'items': orderCartItems.map((d) => ({
            pid: d.productId,
            id: d.commodityId,
            number: d.count,
            bind_services:
              d.bind_services?.map((s) => ({
                id: s.id,
                number: d.count,
                type: s.serviceType,
              })) ?? undefined,
          })),
        }
      : {
          prePaymentChannel: _payment.type === 'paypal' ? 'paypal_ss' : _payment.type,
          orderNumber: simpleDetailOrder.orderNumber,
        };

    return httpRequest({
      url: SERVER.API_SERVER_TRADE + '/getPaymentDiscount',
      method: 'POST',
      data,
    })
      .then((res = {}) => {
        if (res.code === 0 && res.data?.existDiscount) {
          _orderCount.paypalDiscount = res.data?.paymentChannelDiscountAmount ?? 0;
          return res.data?.paymentChannelDiscountAmount;
        } else {
          _orderCount.paypalDiscount = 0;
          return 0;
        }
      })
      .catch(() => {
        _orderCount.paypalDiscount = 0;
        return 0;
      });
  };

  /**
   * 获取信用卡的支付规则，走什么支付渠道，如果无配置则默认钱海
   * @param {*} data
   * @returns {Promise} Promise
   */
  @action
  getPaymentRule = (params) => {
    const { isCloudUpgradeOrderConfirmPage, isSubscriptionPage } = this.root.app;
    const cloudSubscribePaymentRule = isCloudUpgradeOrderConfirmPage || isSubscriptionPage;

    return httpRequest({
      url:
        SERVER.API_SERVER_TRADE +
        `/payment/${cloudSubscribePaymentRule ? 'getSubscribePaymentRule' : 'getPaymentRule'}`,
      ...(cloudSubscribePaymentRule ? { instaToken: true } : {}),
      params,
    }).then((res) => {
      if (res.code === 0) {
        if (cloudSubscribePaymentRule) {
          return res.data;
        } else return res.data.payRule;
      } else if (isEmpty(res)) {
        return {
          paymentMethod: 'ocean',
        };
      }
      return false;
    });
  };

  /**
   * 查询信用卡终端的pk，每个终端的pk不一样
   * 如果是二次支付、云存主动续费场景，pk需要复用上一笔订单的通道。
   */
  @action
  getOceanPaymentPublicKey = (data) => {
    const orderNumber = this.simpleDetailOrder?.orderNumber ?? undefined;
    return httpRequest({
      url: SERVER.API_SERVER_TRADE + '/payment/oceanPayPublicKey',
      method: 'post',
      data: {
        ...data,
        orderNumber,
      },
    });
  };

  /**
   * 钱海支付调用接口
   * @param {boolean} three_domain_trade 是否执行3d校验 true执行
   * @param {*} paymentType 调用的支付类型
   * @param {*} data
   * @param {*} orderNumber
   */
  @action
  submitOceanPayment = (paymentType, data, orderNumber) => {
    const url = SERVER.API_SERVER_PAYMENT + '/ocean/' + paymentType;
    httpRequest({
      url,
      method: 'post',
      data,
    })
      .then((res) => {
        if (res.code === 0) {
          const { pay_url, order_number } = res.data;
          let redirectUrl = '';
          if (pay_url) {
            redirectUrl = pay_url;
          } else {
            redirectUrl = `/order/result/${encodeURIEncryptFE(order_number)}?p=ocean`;
          }
          window.location.href = redirectUrl;
        } else {
          window.location.href = `/order/result/${encodeURIEncryptFE(orderNumber)}`;
        }
      })
      .catch(() => {
        window.location.href = `/order/result/${encodeURIEncryptFE(orderNumber)}`;
      });
  };

  /**
   * checkout支付调用接口
   * @param {*} paymentType
   * @param {*} data
   * @param {*} orderNumber
   */
  @action
  submitCheckoutPayment = (paymentType, data, order_number) => {
    const _paymentType = paymentType[0].toUpperCase() + paymentType.slice(1); // 首字母大写
    const url = SERVER.API_SERVER_PAYMENT + '/checkout/cko' + _paymentType;
    httpRequest({
      url,
      method: 'post',
      data,
    })
      .then((res) => {
        if (res.code === 0) {
          const { payUrl, orderNumber } = res.data;

          let redirectUrl = '';
          // 如果返回order_number表示请款成功，直接跳转结果页，否则跳转支付页面
          if (payUrl) {
            redirectUrl = payUrl;
          } else {
            redirectUrl = `/order/result/${encodeURIEncryptFE(orderNumber)}?p=cko`;
          }
          window.location.href = redirectUrl;
        } else {
          window.location.href = `/order/result/${encodeURIEncryptFE(order_number)}`;
        }
      })
      .catch(() => {
        window.location.href = `/order/result/${encodeURIEncryptFE(order_number)}`;
      });
  };

  /**
   * 云存订阅支付失败的主动支付调用接口
   * 和钱海信用卡支付区别在于 内部增加封装了续费订单的校验，但是最后都是走的同一个钱海支付逻辑。
   * @param {boolean} three_domain_trade 是否执行3d校验 true执行
   * @param {*} paymentType 调用的支付类型
   * @param {*} data
   * @param {*} orderNumber
   */
  @action
  submitOceanRenewOrderPayment = (data, orderNumber) => {
    const url = SERVER.API_SERVER_PAYMENT + '/ocean/subscribe/renewOrderPay';
    httpRequest({
      url,
      method: 'post',
      data,
      instaToken: true,
    })
      .then((res) => {
        if (res.code === 0) {
          const { pay_url, order_number } = res.data;
          let redirectUrl = '';
          if (pay_url) {
            redirectUrl = pay_url;
          } else {
            redirectUrl = `/subscription/management?result=${encodeURIEncryptFE(order_number)}`;
          }
          window.location.href = redirectUrl;
        } else {
          window.location.href = `/subscription/management?result=${encodeURIEncryptFE(orderNumber)}`;
        }
      })
      .catch(() => {
        window.location.href = `/subscription/management?result=${encodeURIEncryptFE(orderNumber)}`;
      });
  };

  /**
   * @description 获取支付渠道和地区通道，获取规则有4种场景
   * 1. 常规结账页支付————调用getPaymentRule接口获取
   * 2. 云存升级结账页支付————调用getSubscribePaymentRule接口获取
   * 3. 云存升级二次支付页————直接使用simpleDetailOrder.orderPaymentInfo
   * 4. 云存续费失败主动支付————调用getSubscribePaymentRule接口获取
   * @return {Promise} Promise
   */
  getPaymentRuleAdapter = async () => {
    const {
      _payment: { creditCardName },
      detailOrder: { order_number },
      simpleDetailOrder,
    } = this;

    let payRule = {};
    // 这里判断是否为云存升级二次支付场景，二次支付复用之前的支付通道，从simpleDetailOrder中获取
    if (simpleDetailOrder?.subscribeScenesType === 'UPGRADE' && simpleDetailOrder.orderPaymentInfo) {
      const { paymentChannelId, paymentMethod } = simpleDetailOrder.orderPaymentInfo;
      payRule = {
        paymentChannelId,
        paymentMethod,
      };
      if (!paymentChannelId || !paymentMethod) {
        payRule = {};
      }
      return payRule;
    }

    // 常规处理：1. 结账页支付。2. 云存升级结账页支付。3. 云存续费失败主动支付
    payRule = await this.getPaymentRule({
      orderNumber: order_number,
      cardType: creditCardName.toLowerCase(),
    });

    return payRule;
  };

  /**
   * 信用卡提交支付前置处理：1、payrule 2、Forter风控。3、3、根据商品信息判断支付模式payMode（没有商品信息则不传）4、根据渠道调用接口。
   * 信用卡支付场景：常规结账支付、云存升级。
   */
  @action
  submitCreditCard = async () => {
    try {
      const {
        _payment: { cardNumber, cardHolder, creditCardName, cardMonth, cardYear, cardSecureCode, cardData },
        detailOrder: { order_number },
      } = this;
      const card_number = strTrim(cardNumber);
      const bin = card_number.substring(0, 6);
      const lastFourDigits = card_number.substring(card_number.length - 4, card_number.length);
      // 无items的场景为云存储更换卡信息
      const payMode = this.hasCloudServiceProduct ? 2 : 1;

      // 支付失败处理函数
      const handleForterAdaptiveAuthException = () => {
        browserPushUrl(`/order/result/${encodeURIEncryptFE(order_number)}`);
        this.setStateValue('_beingPaid', false);
      };

      // 获取支付渠道和地区通道
      const payRule = await this.getPaymentRuleAdapter().catch(() => {
        handleForterAdaptiveAuthException();
      });

      if (!cardData || isEmpty(payRule)) {
        return this.setStateValue('_beingPaid', false);
      }
      // 美国的钱海订阅通道没有下来，所以暂时订阅支付信用卡全部抛送cko
      const isOcean = payRule.paymentMethod === 'ocean';
      // 调用checkout / Ocean 接口的参数
      let paymentPayload = {};

      this.setStateValue('_beingPaid', true);

      await preCheckForterToken(this.root.app);

      // 前置风控查询
      this.forterAdaptiveAuth({
        orderNumber: order_number,
        nameOnCard: cardHolder,
        bin,
        lastFourDigits,
        payChannelId: payRule.paymentChannelId,
        cardType: creditCardName.toLowerCase(),
      })
        .then((res) => {
          const { status, result } = res || {};
          switch (status) {
            case 'reject':
              // 抛异常均跳转到result
              // DECLINE -- forter拒绝该订单，接口直接取消该订单
              handleForterAdaptiveAuthException();
              break;
            case 'approve':
              const commonPayload = {
                orderNumber: order_number,
                threeDomainTrade: result === forterResult.threeDomainTrade,
                exemption: forterResult.exemption.includes(result),
              };
              if (isOcean) {
                paymentPayload = {
                  ...commonPayload,
                  creditCardName,
                  cardData,
                  payMode,
                };

                this.submitOceanPayment('creditCard', paymentPayload, order_number);
              } else {
                paymentPayload = {
                  ...commonPayload,
                  cardMonth,
                  cardYear,
                  name: cardHolder,
                  cardNumber: encryptBE(cardNumber),
                  cardCvv: encryptBE(cardSecureCode),
                  paymentChannelId: payRule.paymentChannelId,
                  payMode,
                };
                this.submitCheckoutPayment('creditCard', paymentPayload, order_number);
              }
              break;
            default:
          }
        })
        .catch((e) => {
          feishuRobotSend({ err: e, text: [`【forter to pay error:${order_number}】`], at: ['dxy'] });
          handleForterAdaptiveAuthException();
        });
    } catch (e) {
      this.setStateValue('_beingPaid', false);
      this.setStateValue('createOrderLoading', false);
      feishuRobotSend({
        err: e,
        text: [
          `信用卡支付处理失败`,
          `订单号：${this?.detailOrder?.order_number}`,
          `_payment:${JSON.stringify(this._payment ?? {})}`,
        ],
        at: ['dxy', 'ccb'],
      });
    }
  };

  /**
   * 当地的支付机构，调用时跳转第三方链接 类型：sofortBanking、iDEAL、kakao、naver
   * Ocean：返回一个表单，前端提交至第三方，Cko：同信用卡，返回一个链接地址
   * 目前本地支付仅支持钱海，关闭payrule查询权重分配（后续支付方式接入后再变动）
   * ——关闭原因：用户第一次选择信用卡支付后本订单支付规则分配至cko支付，后续换成本地支付仍然走cko支付造成支付失败。
   * @param {} paymentType paymentType
   */
  @action
  submitLocalCreditCard = async (paymentType) => {
    const {
      detailOrder: { order_number },
    } = this;

    // 根据获取的支付规则决定使用什么样的支付渠道和地区通道，不要删掉，后续可能重新启用
    // const payRule = await this.getPaymentRule({
    //   orderNumber: order_number,
    //   cardType: paymentType.toLowerCase(),
    // });
    // if (!payRule) {
    //   return this.setStateValue('_beingPaid', false);
    // }
    // const isOcean = payRule.paymentMethod === 'ocean';
    const payRule = {};
    const isOcean = true;

    if (isOcean) {
      const url = SERVER.API_SERVER_PAYMENT + '/ocean/' + paymentType;
      httpRequest({
        url,
        method: 'post',
        responseType: 'html',
        data: { orderNumber: order_number },
      }).then((res) => {
        // 提交接口返回的表单
        window.document.write(res);
        document.getElementById('payForm').submit();
      });
    } else {
      const url = SERVER.API_SERVER_PAYMENT + '/checkout/' + paymentType;
      httpRequest({
        url,
        method: 'post',
        data: {
          orderNumber: order_number,
          paymentChannelId: payRule.paymentChannelId,
        },
      })
        .then((res) => {
          if (res?.code === 0) {
            const { payUrl, orderNumber } = res.data;
            window.location.href = payUrl || `/order/result/${encodeURIEncryptFE(orderNumber)}`;
          }
        })
        .catch(() => {
          window.location.href = `/order/result/${encodeURIEncryptFE(order_number)}`;
        });
    }
  };

  /**
   * 订阅云存的信用卡支付
   * 信用卡提交支付前置处理：1、payRule 2、 Forter风控 3、支付模式payMode为订阅场景
   * @param payload 支付方式判断入参
   * @param payload.adaptiveType 当前支付是什么类型场景（比如云存主动支付 / 或者其他的场景）
   */
  @action
  submitCloudSubscribeCreditCard = async (payload = {}) => {
    try {
      const {
        _payment: { cardNumber, cardHolder, creditCardName, cardData },
        detailOrder: { order_number },
      } = this;
      const card_number = strTrim(cardNumber);
      const bin = card_number.substring(0, 6);
      const lastFourDigits = card_number.substring(card_number.length - 4, card_number.length);

      // 支付失败处理函数
      const handleForterAdaptiveAuthException = () => {
        const cancelResultUrl = `/subscription/management?result=${encodeURIEncryptFE(order_number)}`;
        browserPushUrl(cancelResultUrl);
        this.setStateValue('_beingPaid', false);
      };

      // 获取支付渠道和地区通道
      const payRule = await this.getPaymentRuleAdapter().catch(() => {
        handleForterAdaptiveAuthException();
      });

      if (!cardData || isEmpty(payRule)) {
        return this.setStateValue('_beingPaid', false);
      }
      // 美国的钱海订阅通道没有下来，所以暂时订阅支付信用卡全部抛送cko
      const isOcean = payRule.paymentMethod === 'ocean';
      // 调用checkout / Ocean 接口的参数
      let paymentPayload = {};

      this.setStateValue('_beingPaid', true);

      await preCheckForterToken(this.root.app);

      // 如果payRule不是ocean，又是主动支付订单，需要报错
      if (!isOcean) {
        feishuRobotSend({ text: ['订单主动支付但是渠道不是ocean', `data:${order_number}`], at: ['dxy'] });
      }

      // 前置风控查询
      this.forterAdaptiveAuth(
        {
          orderNumber: order_number,
          nameOnCard: cardHolder,
          bin: encryptBE(bin),
          lastFourDigits: encryptBE(lastFourDigits),
          payChannelId: payRule.paymentChannelId,
          cardType: creditCardName.toLowerCase(),
          adaptiveAuthType: OrderToPayAdaptiveEnum[payload?.adaptiveType],
        },
        'subscription'
      )
        .then((res) => {
          const { status, result } = res || {};
          switch (status) {
            case 'reject':
              // 抛异常均跳转到result
              // DECLINE -- forter拒绝该订单，接口直接取消该订单
              handleForterAdaptiveAuthException();
              break;
            case 'approve':
              if (isOcean) {
                paymentPayload = {
                  orderNumber: order_number,
                  threeDomainTrade: result === forterResult.threeDomainTrade,
                  exemption: forterResult.exemption.includes(result),
                  creditCardName,
                  cardData,
                  payMode: 2,
                };

                this.submitOceanRenewOrderPayment(paymentPayload, order_number);
              }

              break;
            default:
          }
        })
        .catch((e) => {
          feishuRobotSend({
            err: e,
            text: [`【cloud subscription forter to pay error:${order_number}】`],
            at: ['dxy'],
          });
          handleForterAdaptiveAuthException();
        });
    } catch (e) {
      this.setStateValue('_beingPaid', false);
      this.setStateValue('createOrderLoading', false);
      feishuRobotSend({
        err: e,
        text: [
          `主动续费的信用卡支付处理失败`,
          `订单号：${this?.detailOrder?.order_number}`,
          `_payment:${JSON.stringify(this._payment ?? {})}`,
        ],
        at: ['dxy', 'ccb'],
      });
    }
  };

  /**
   * 本土支付但是非信用卡支付，默认ocean支付方式
   * get请求 返回payUrl跳转
   * @param {*} paymentType
   * @param {orderNumber,payMode} payload
   */
  @action
  submitLocalPayment = (paymentType, { orderNumber, payMode }) => {
    httpRequest({
      url: `${SERVER.API_SERVER_PAYMENT}/${paymentType}/${paymentType}?orderNumber=${orderNumber}&payMode=${payMode}`,
    })
      .then((res) => {
        if (res?.code === 0) {
          const { payUrl, orderNumber } = res.data;
          window.location.href = payUrl || `/order/result/${encodeURIEncryptFE(orderNumber)}`;
        }
      })
      .catch(() => {
        window.location.href = `/order/result/${encodeURIEncryptFE(orderNumber)}`;
      });
  };

  @action
  getWechatPayment = (params, callback) => {
    const url = `${SERVER.API_SERVER_PAYMENT}/wechat/${params.channel}`;
    httpRequest({
      url,
      params,
    })
      .then((res) => {
        if (res.code === 10001) {
          // 后台微信接口超时，刷新重新请求
          window.location.reload();
          return;
        }
        if (res && res.codeURL) {
          this.order_wechat_qr_payment = res.codeURL;
        }
        if (callback) {
          callback(res);
        }
      })
      .catch(() => {
        window.location.reload();
      });
  };

  @action
  getWechatQrPaymentState = (orderNumber, callback) => {
    const url = SERVER.API_SERVER_PAYMENT + '/wechat/notify/weiXinQrLoop';
    return httpRequest({
      url,
      params: {
        orderNumber,
      },
    }).then((res) => {
      callback(res);
      if (res && res.flag) {
        this.order_wechat_qr_payment_number = orderNumber;
      }
    });
  };

  @action
  useCouponCode = (data) =>
    new Promise((resolve) => {
      const dataItemsAfterFilter = this.handleCareServiceForUS({ type: 'filterCare', items: data.items });
      data.items = dataItemsAfterFilter;

      httpRequest({
        url: SERVER.API_SERVER_TRADE + '/coupon/use',
        method: 'post',
        instaToken: true,
        data,
      }).then((res) => {
        resolve(res);
      });
    });

  @action
  getUserGiftCards = () => {
    if (!this.root.auth.isLoggedIn) return;
    httpRequest({
      url: SERVER.API_SERVER_TRADE + '/coupon/getUserGiftCards',
      instaToken: true,
      params: {
        include_used: false, // default:true
        include_expired: false, // default:true
      },
    })
      .then((res) => {
        if (res.code === 0) {
          this.userGiftCards = res?.data?.list ?? [];
        }
      })
      .catch((e) => {
        console.log(e);
      });
  };

  // 获取中国大陆省份城市二级下拉
  @action
  getLocationsArea = () =>
    new Promise((resolve) => {
      if (this.locationsArea && this.locationsArea.length > 0) {
        resolve(this.locationsArea);
        return;
      }
      const url = SERVER.API_SERVER_META + '/location/getLocations';
      httpRequest({
        url,
        X_Country_Code: 'CN',
        X_Language: 'zh_cn',
      }).then((res) => {
        this.locationsArea = res.data.locations;
        resolve(res.data.locations);
      });
    });

  // 获取州代码，ocean_code用在(海外)账单地址中，钱海支付
  @action
  getProvinceOceanCodes = (CountryCode) => {
    const country_code = CountryCode || this.root.area.country.code;
    return new Promise((resolve) => {
      if (this.ProvinceOceanCodes[country_code]) {
        resolve(this.ProvinceOceanCodes[country_code]);
        return;
      }
      this.fetchingProvince = true;
      httpRequest({
        url: SERVER.API_SERVER_META + `/ocean/getOceanCountryConfig`,
        X_Country_Code: country_code,
        X_Language: '',
      })
        .then(({ data }) => {
          if (data.ocean_country_config.length > 0) {
            data.ocean_country_config.forEach((o) => {
              o.name = o.province_name;
            });
            this.ProvinceOceanCodes[country_code] = data.ocean_country_config;
            resolve(data.ocean_country_config);
          }
        })
        .catch((err) => {
          window?.monitorCore?.sendEvent({
            type: 'ORDER_CONFIRM',
            name: 'getProvinceOceanCodes',
            group: 'ORDER_CONFIRM',
            value: err?.message || 'Http request error. /getOceanCountryConfig',
          });
        })
        .finally(() => (this.fetchingProvince = false));
    });
  };

  // 获取国际区号代码列表
  @action
  getPhoneCodes = () => {
    return new Promise((resolve) => {
      if (this.PhoneCodes.length > 0) {
        resolve(this.PhoneCodes);
        return;
      }
      httpRequest({
        url: SERVER.API_SERVER_USER + `/delivery/listPhoneCode`,
      })
        .then(({ data }) => {
          if (data) {
            this.PhoneCodes = data.phoneCodes;
            resolve(data.phoneCodes);
          }
        })
        .catch(() => {
          this.PhoneCodes = [];
        });
    });
  };

  @action
  setFormFromDeliveryFrom = (thisType, thisForm, cb) => {
    this.form[thisType] = thisForm;
    cb && cb();
  };

  @action
  validateOrderForm = async (type) => {
    return new Promise((resolve) => {
      if (!this.form[type]) resolve(true);
      this.form[type]
        .validateFields()
        .then((values) => {
          console.log(`Success Validate Order form Fields ${type}：`, values);
          resolve(true);
        })
        .catch(({ errorFields }) => {
          console.log(`Error Validate Order form Fields ${type}：`, errorFields);
          if (!errorFields) resolve(true);
          const id = errorFields[0].name.toString();
          scrollToEle(`#${id}`);
          resolve(false);
        });
    });
  };

  // 同时获取（同种类型文件的）多个验签信息（一次最多10个），fileName必须要带后缀，返回的验签信息fileName在此基础上修改
  // 通过请求头X-Country确认选择上传上海 or 硅谷节点
  getCommonUploadInfo = (fileSize, fileName, signatureNumber = 1) => {
    const url = SERVER.API_SERVER_META + '/oss/listSignatures';
    return httpRequest({
      url,
      params: {
        fileSize, // 文件大小 超过很大的数将可能拒绝
        fileName,
        signatureNumber,
      },
    });
  };

  // 上传文件到OSS,可获取上传进度、可以主动取消请求
  // 文件CDN连接域名：media.insta360.com 视频，res.insta360.com 图片，file.insta360.com 大文件（暂时没有用到）
  @action
  uploadFileToOSS = (props = {}, onUploadProgress, cancelToken) => {
    const data = new FormData();
    data.append('OSSAccessKeyId', props.accessId);
    data.append('expire', props.expire);
    data.append('key', `${props.filePath}/${props.fileName}`);
    data.append('policy', props.policy);
    data.append('signature', props.signature);
    data.append('file', props.file);

    return httpRequest({
      url: props.host,
      method: 'POST',
      data,
      onUploadProgress,
      cancelToken,
    });
  };

  @action
  saveCustomImage = (data, callback) => {
    const url = SERVER.API_SERVER_TRADE + '/custom/saveCustomImage';
    httpRequest({
      url,
      method: 'post',
      data,
    }).then((res) => {
      callback(res);
    });
  };

  // 新售后接口
  @action
  refundRmaOrder = (data, cb) => {
    const url = SERVER.API_SERVER_RMA + '/order/refundApply';
    httpRequest({
      url,
      method: 'post',
      data,
      instaToken: true,
    }).then((res) => {
      cb && cb(res);
    });
  };

  @action
  getRmaRefundApplyOptions = (data, cb) => {
    const url = SERVER.API_SERVER_RMA + '/order/applyRefundPreView';
    httpRequest({
      method: 'post',
      instaToken: true,
      url,
      data,
    }).then((res) => {
      cb && cb(res);
    });
  };

  @action
  getRmaOrderReasonOptions = (params, cb) => {
    const url = SERVER.API_SERVER_RMA + '/order/getReasonOptions';
    httpRequest({
      url,
      params,
    }).then((res) => {
      cb && cb(res);
    });
  };

  @action
  getRmaOrderDetail = (params, cb) => {
    const url = SERVER.API_SERVER_RMA + '/order/getRmaOrderInfo';
    httpRequest({
      url,
      params,
      instaToken: true,
    }).then((res) => {
      cb && cb(res);
    });
  };

  @action
  sendOffRmaOrderExpress = (data, cb) => {
    const url = SERVER.API_SERVER_RMA + '/order/uploadReturnLogistics';
    httpRequest({
      url,
      method: 'post',
      data,
      instaToken: true,
    }).then((res) => {
      cb && cb(res);
    });
  };

  /**
   * @description 判断是否墨西哥高价值订单限制 关于墨西哥的税费提示和下单限制：凡是超过的1000美元的订单，都不可以下单。点击下单的时候弹出窗口，弹窗文案根据购买内容分成两种：
   *
   * - 购买专业级相机（产品ID：1,6,10,258），提示顾客联系客服。
   * - 购买其他产品，提醒顾客拆分两个订单。
   * @date 2023/03/08 16:38:27
   */
  @action
  checkOrderTaxForMX = () => {
    if (CountryCode !== 'MX') return false;
    const { isOrderConfirmPage } = this.root.app;
    const { handleCalcOrderConfirmPayment } = this;
    this.professionalDiscountItem = null;
    let items = this.selectedCartItems;
    if (isOrderConfirmPage) items = this.orderCartItems;

    // 允许下单产品, 如果购买以下两款商品可放行下单；
    const allowProductIds = [399, 400];
    const hasAllowProduct = allowProductIds.some((pid) => !!items.find((o) => o.productId === pid));
    if (hasAllowProduct) return false;

    // 专业级设备产品id
    const ProProductIds = [1, 6, 10];
    // 专业级设备套餐id
    const ProCommodityIds = [1054, 1055];

    const hasProProduct = ProProductIds.some((pid) => !!items.find((o) => o.productId === pid));
    const hasProCommodity = ProCommodityIds.some((cid) => !!items.find((o) => o.commodityId === cid));

    if (hasProProduct || hasProCommodity) {
      this.taxModalForMXType = 1;
      return true;
    }

    // 高价值订单（过滤虚拟商品）
    let total = 0;
    let isHighValueOrder = false;
    const VirtualCommodityIds = [1053];

    try {
      items.forEach((o) => {
        if (!VirtualCommodityIds.includes(o.commodityId)) {
          total += o?.count * o?.price?.amount;
        }
      });

      // 结账页还需要减去折扣
      if (isOrderConfirmPage) {
        total += handleCalcOrderConfirmPayment.discount || 0;
      }

      isHighValueOrder = total >= 1000;

      if (isHighValueOrder) {
        this.taxModalForMXType = 2;
      }
      return isHighValueOrder;
    } catch (e) {
      return false;
    }
  };

  @action
  commitProfessionalSerial = (data) =>
    httpRequest({
      url: SERVER.API_SERVER_TRADE + '/serial/commit',
      method: 'post',
      data,
      instaToken: true,
    });

  @action
  getSerialGiftCode = (params) =>
    httpRequest({
      url: SERVER.API_SERVER_TRADE + '/serial/getSerialGiftCode',
      instaToken: true,
      params,
    });

  @observable
  professionalDiscountItem = null;

  @action
  getProfessionalDiscount = (cb) => {
    const { isOrderConfirmPage } = this.root.app;

    let items = this.selectedCartItems;
    if (isOrderConfirmPage) items = this.orderCartItems;
    if (items.length === 0) {
      cb && cb(false);
      return;
    }

    const productList = ProfessionalDiscount.getProductList({ LANG: this.root.area.LANG });
    const ProDis = cache.get(`pro_dis`) ? JSON.parse(cache.get(`pro_dis`)) : null;
    if (!ProDis) {
      cb && cb(false);
      return;
    }
    productList.forEach((x) => {
      x.cache = items.filter((o) => o.productId === x.id)[0] && ProDis[x.id];
    });
    const pTitan = productList.filter((o) => o.cache && o.id === 10)[0];
    const pPro2 = productList.filter((o) => o.cache && o.id === 6)[0];
    const itemCache = pTitan || pPro2;
    return itemCache;
  };

  @action
  checkProfessionalDiscountState = (cb) => {
    this.professionalDiscountItem = null;

    const itemCache = this.getProfessionalDiscount(cb);
    if (!itemCache) {
      cb && cb(false);
      return;
    }
    this.getSerialGiftCode({
      device_serial: itemCache.cache.device_serial,
      discount_page: itemCache.cache.discount_page,
    }).then(({ code, data }) => {
      if (code !== 0) return;
      if (data.serial_discount) {
        this.professionalDiscountItem = itemCache;
      }
      cb && cb(!!data.serial_discount);
    });
  };

  @action
  getCartCommodityRecommendation = debounce((data) => {
    if (data && data.commodity_ids && data.commodity_ids.length === 0) {
      this.cart_recommend = [];
      return;
    }
    httpRequestLimitNums({
      url: SERVER.API_SERVER_TRADE + '/cart/getCartCommodityRecommendationInfo',
      method: 'post',
      data,
    })
      .then(({ code, data }) => {
        if (code !== 0) {
          this.cart_recommend = [];
          return;
        }
        if (code === 0 && !isEmpty(data)) {
          const recommend_accessories_info = data.recommend_accessories_info.filter(
            (o) => o.sale_state >= -1 && o.price
          );
          if (recommend_accessories_info.length === 0) {
            this.cart_recommend = [];
          } else {
            // 缺货套餐只显示原价
            const outOfStockC = recommend_accessories_info.filter((o) => o.sale_state === -1);
            outOfStockC.forEach((c) => {
              if (c?.origin_price) {
                c.price = c.origin_price;
                delete c.origin_price;
              }
            });

            this.cart_recommend = [...recommend_accessories_info.filter((o) => o.sale_state >= 0), ...outOfStockC];
          }
        }
      })
      .catch(() => {
        this.cart_recommend = [];
      });
  }, 500);

  @action
  handleUserInvoice = ({ type = 'get', key, val }) => {
    const tempCache = cache.get('user_invoice');
    const userInvoice = tempCache ? JSON.parse(tempCache) : {};
    switch (type) {
      case 'get':
        return userInvoice[key];
      case 'set':
        this._orderInvoice = {
          type: key,
          done: true,
          info: val,
        };
        if (key !== 'none') {
          userInvoice[key] = val;
          cache.set('user_invoice', JSON.stringify(userInvoice));
        }
        break;
      case 'reset':
        this._orderInvoice = {
          type: 'none',
          done: true,
          info: {},
        };
        break;
      default:
    }
  };

  /*
   *
   * @description 根据货币获取对公转账账户信息
   * @param {string} currency 货币类型
   * @return {type} value
   * @time: 2021/7/14 3:00 下午
   *
   */
  @action
  getTransferInfo = ({ currency }) =>
    new Promise((resolve) => {
      httpRequest({
        url: SERVER.API_SERVER_META + '/info/getTransferInfo',
        params: {
          currency,
        },
      }).then((res) => {
        resolve(res);
      });
    });

  /**
   *
   * @description 下单完成后下次访问页面，删除已下单的商品
   */
  @action
  resetAfterOrderCreate = () => {
    try {
      if (!cache.getCookie('need_reset_cart')) return;
      cache.removeCookie('need_reset_cart');

      // 获取购物车中已下单的商品 && 从购物车删除
      const orderCartItemPids = this.orderCartItems.map((item) => item.productId);
      const cartItemAfterRemove = this.allCartItems.filter((item) => !orderCartItemPids.includes(item.productId));
      this.setCartItems(cartItemAfterRemove, true);

      // 清空结账页商品 & 云服务升级结账页
      this.setOrderCartItems([]);
    } catch (e) {
      // 清空结账页商品 & 云服务升级结账页
      this.setOrderCartItems([]);
      armsErrorSend('清空结账页商品失败', {
        filename: 'Mobx Order',
        data: { errMsg: e, orderCartItems: this.orderCartItems },
      });
    }
  };

  /**
   * 处理二次支付页选择支付优惠时的订单金额逻辑 被order/pay,order/detail页面调用
   */
  @action
  handleRepayOrderPaypalDiscount = async () => {
    const {
      campaignWrap: { campaignWrap },
    } = this.root.app;
    const {
      getRepayConsumptionTax,
      setStateValue,
      simpleDetailOrder,
      simpleDetailOrder: {
        orderNumber,
        province,
        originPaymentInfo,
        orderItemInfos,
        area,
        paypalPaymentInfo,
        orderPaymentInfo,
      },
    } = this;
    if (isEmpty(simpleDetailOrder)) return;

    // 目前仅有paypal 5%off活动会引起减价
    if (campaignWrap?.PaypalSale && !campaignWrap.PaypalSale?.checkItemList(orderItemInfos, area)) {
      if (this._payment?.type === 'paypal') {
        const isTaxesState = this.taxesStateData.includes(province?.toUpperCase());
        if (paypalPaymentInfo) {
          simpleDetailOrder.orderPaymentInfo = paypalPaymentInfo;
        } else if (['US', 'CA'].includes(area) && isTaxesState) {
          // 首次选择paypal，处理收税州数据
          getRepayConsumptionTax({
            countryCode: area,
            orderNumber,
            province,
            prePaymentChannel: 'paypal_ss',
          })?.then(async (_d) => {
            if (_d.delivery_tax?.validateTax) {
              const { taxFee, totalAmount } = orderPaymentInfo;

              const paypalDiscount = (await this.getPaymentDiscount()) ?? 0;
              const _totalAmount = formatPrice(
                totalAmount - paypalDiscount * 1 - taxFee * 1 + (_d.delivery_tax?.salesTax ?? taxFee) * 1
              );

              simpleDetailOrder.paypalPaymentInfo = {
                ...originPaymentInfo,
                totalAmount: _totalAmount,
                taxFee: _d.delivery_tax?.salesTax ?? taxFee,
                paypalDiscount,
              };
              simpleDetailOrder.orderPaymentInfo = simpleDetailOrder.paypalPaymentInfo;
            } else {
              simpleDetailOrder.orderPaymentInfo = originPaymentInfo;
            }
          });
        } else {
          // 处理非收税州地区 & 非US / CA地区，非消费税地区的paypal折扣数据
          const { totalAmount } = orderPaymentInfo;

          const paypalDiscount = (await this.getPaymentDiscount()) ?? 0;
          simpleDetailOrder.paypalPaymentInfo = {
            ...originPaymentInfo,
            paypalDiscount,
            totalAmount: formatPrice(totalAmount * 1 - paypalDiscount * 1),
          };
          simpleDetailOrder.orderPaymentInfo = simpleDetailOrder.paypalPaymentInfo;
        }
      } else {
        simpleDetailOrder.orderPaymentInfo = originPaymentInfo;
      }
      setStateValue(simpleDetailOrder);
    }
  };

  /**
   * @desc 更新云服务订阅卡信息或者重新订阅，所有的支付渠道共用，只是变更body参数
   * @param type updateCard | renewSubscribe
   * @param errorCallback 错误处理
   * @param data 参数(paypal支付等是可以不传data的)
   */
  updateOrRenewSubscribe = (type, errorCallback, data) => {
    const payment = this.root.app.cloudSubscribeInfo?.subscribeDetail?.cloudSubscribePaymentInfo?.paymentMethod;

    return httpRequest({
      url:
        SERVER.API_SERVER_CLOUD +
        (type === 'updateCard' ? '/updateSubscribeInformation' : type === 'renewSubscribe' ? '/renewSubscribe' : ''),
      method: 'post',
      data,
      instaToken: true,
    }).then((res = {}) => {
      if (res.code === 0) {
        const { approveUrl } = res.data || {};
        let redirectUrl = '';
        if (approveUrl) {
          redirectUrl = approveUrl;
        } else {
          redirectUrl = `/subscription/management?result=${encodeURIEncryptFE(
            `key=${payment}&result=true&action=${type}`
          )}`;
        }
        window.location.href = redirectUrl;
      } else {
        errorCallback?.(res);
      }
      return res;
    });
  };

  /**
   * 信用卡更新云服务订阅卡信息或者重新订阅
   * @param type updateCard | renewSubscribe
   * @param errorCallback 错误处理
   */
  @action
  creditCardUpdateOrRenewSubscribe = async (type, errorCallback) => {
    const { cardNumber, cardHolder, creditCardName, cardData, cardSecureCode, cardMonth, cardYear } = this._payment;
    const card_number = strTrim(cardNumber);
    const bin = card_number.substring(0, 6);
    const lastFourDigits = card_number.substring(card_number.length - 4, card_number.length);

    await preCheckForterToken(this.root.app);

    return this.forterAdaptiveAuth(
      {
        nameOnCard: cardHolder,
        bin: encryptBE(bin),
        lastFourDigits: encryptBE(lastFourDigits),
        cardType: creditCardName.toLowerCase(),
      },
      'subscription'
    ).then(({ status, result }) => {
      switch (status) {
        case 'reject': // 拒绝
          errorCallback();
          break;
        case 'approve': // 3D校验通过
          const payment = this.root.app.cloudSubscribeInfo?.subscribeDetail?.cloudSubscribePaymentInfo?.paymentMethod;
          let data = {};
          if (payment === 'ocean') {
            data = {
              creditCardName: creditCardName.toLowerCase(),
              cardData,
            };
          } else if (payment.includes('Cko')) {
            data = {
              threeDomainTrade: result === forterResult.threeDomainTrade,
              exemption: forterResult.exemption.includes(result),
              cardMonth,
              cardYear,
              nameOnCard: cardHolder,
              cardNumber: encryptBE(cardNumber),
              cardCvv: encryptBE(cardSecureCode),
            };
          }
          this.updateOrRenewSubscribe(type, errorCallback, data);
          break;
        default:
          errorCallback();
          break;
      }
    });
  };

  /**
   * 取消订阅
   */
  @action
  cancelCloudSubscribe = (cancelReason) => {
    return httpRequest({
      url: SERVER.API_SERVER_CLOUD + '/cancelCloudSubscribe',
      method: 'post',
      data: {
        cancelReason,
      },
      instaToken: true,
    });
  };

  /**
   * 获取游客是否存在对应的账号在订阅期，如果code=16001则已经有订阅期账号不可购买，其他异常则为可购买
   */
  getGuestSubscribeState = (email) => {
    return httpRequest({
      url: SERVER.API_SERVER_CLOUD + '/cloudSubscribeVisitorCheck',
      params: { email },
    }).catch(() => {
      return {
        code: 0,
      };
    });
  };

  /**
   * 获取云存储账号 / 未登陆用户对应账号 的账号所属地 & 所属地的可选地区
   */
  getSubscribeUserRegion = (email) => {
    const { allCountry } = this.root.area;
    return httpRequest({
      url: SERVER.API_SERVER_CLOUD + '/getUserRegionConfig',
      params: { email },
      instaToken: true,
    })
      .then((res) => {
        if (res.code === 0 && res.data) {
          res.data.availableRegions = res.data.availableRegions
            ? res.data.availableRegions
                .map((countryCode) => {
                  const countryDetail = allCountry.find((d) => d.code === countryCode);
                  if (countryDetail) {
                    return {
                      value: countryDetail.code,
                      label: countryDetail.name,
                    };
                  }

                  return null;
                })
                .filter((d) => !!d)
            : undefined;

          if (res.data.storeRegion) {
            res.data.storageRegion =
              allCountry.find((d) => d.code === res.data.storeRegion)?.name ?? res.data.storeRegion;
          }
          return res.data;
        }
      })
      .catch((e) => {
        console.error(e);
      });
  };

  /**
   * 更新云存储账号的账单信息(参数驼峰)
   */
  updateSubscribeBillingAddress = (data) => {
    return httpRequest({
      url: SERVER.API_SERVER_CLOUD + '/updateSubscribeBillingAddress',
      instaToken: true,
      method: 'post',
      data,
    });
  };

  /**
   *
   * @description 标记当前结账页商品是否包含care，若为US地区且存在则结账页展示赠品文案
   * - 如果结账页本身已经存在加购care，则提示赠品A文案；
   * - 如果结账页本身不存在加购care，则提示赠品B文案；
   */
  @observable
  showCareGiftTips = false; // false 不显示 || A 显示A文案 || B 显示B文案

  /**
   *
   * @description 获取和存储当前结账页商品匹配的care || care_plus套餐信息，用于结账页商品清单展示赠品使用，并用于下单时封装otherItems字段
   */
  @observable
  careServiceOnOrderCartItems = [];

  @action
  handleMatchOrderCartItemsCareGroup = () => {
    let matchCareGroup = [];
    this.orderCartItems.forEach((item) => {
      const curItemCid = item.commodityId;
      const matchCare = item.bindClimbServices?.find?.((o) =>
        [ExtraServiceType.CARE, ExtraServiceType.CARE_PLUS].includes(o.serviceType)
      );
      if (matchCare?.serviceCommodityId) {
        matchCareGroup.push({
          id: matchCare.serviceCommodityId,
          commodityId: curItemCid,
          count: item.count,
          matchCareId: matchCare.serviceCommodityId,
          serviceType: matchCare.serviceType,
        });
      }
    });

    return matchCareGroup;
  };

  @action
  handleCareServiceForUS = ({ type = 'hasCare', items = [], listItem = null, careServiceList = [] }) => {
    const { country } = this.root.area;
    const { Q4BFGiftsCareForUS } = this.root.app.campaignWrap.campaignWrap;

    const isUSOnConfirmPage = country.code === 'US' && this.root.app.isOrderConfirmPage;
    // 不赠送实验方案
    // const disabledCareGift = this.root.abtest.isTargetingResult('checkout_care_gift', 'compact1');
    // 全量不赠送
    const disabledCareGift = true;

    // 判断当前结账页清单是否存在care增值服务，如果有的话打个标记用于展示赠品tips, 且持久化状态到本地避免刷新后导致丢失
    // 所有主机都加购则为A
    // 否则为B
    if (type === 'hasCare') {
      if (!isUSOnConfirmPage || disabledCareGift) return;
      if (Q4BFGiftsCareForUS?.enable()) return;

      // 匹配当前购物清单是否存在含care的商品
      const matchCareGroup = this.handleMatchOrderCartItemsCareGroup();
      if (!matchCareGroup.length) return;

      const isReload = performance?.getEntriesByType('navigation')?.[0]?.type === 'reload';
      const showCareGiftTipsCache = window.localStorage.getItem('showCareGiftTips');

      // 如果是刷新，则取本地缓存
      if (isReload && showCareGiftTipsCache) {
        this.setStateValue('showCareGiftTips', showCareGiftTipsCache);
        return;
      }

      // 判断是否所有主机都加购含care || care_plus
      let allHasCare = true;
      this.orderCartItems.forEach((o) => {
        // 当前SKU是否包含care || care_plus
        const itemMatchCareService = o.bind_services?.some?.((o) =>
          [ExtraServiceType.CARE, ExtraServiceType.CARE_PLUS].includes(o.serviceType)
        );

        if (!itemMatchCareService) {
          allHasCare = false;
        }
      });

      const tipsType = allHasCare ? 'A' : 'B';
      this.setStateValue('showCareGiftTips', tipsType);
      window?.localStorage?.setItem('showCareGiftTips', tipsType);
    }

    // 获取当前结账页商品匹配的care，并通过getInfos获取详情，作为单独的赠品展示在产品列表
    if (type === 'matchCareService') {
      if (!isUSOnConfirmPage || disabledCareGift) return;
      // Q4大促期间不生效，通过活动赠品进行配置
      if (Q4BFGiftsCareForUS?.enable()) return;

      let matchCareGroup = this.handleMatchOrderCartItemsCareGroup();
      const mIds = matchCareGroup.map((o) => o.matchCareId);
      // 获取care列表后封装套餐请求
      mIds.length > 0 &&
        this.root.product.getCommodityPrices(mIds, (res) => {
          if (res?.data?.commodities) {
            matchCareGroup = matchCareGroup.map((o) => {
              return {
                ...o,
                service: res.data.commodities.find((c) => c.id === o.matchCareId),
              };
            });
          }
          this.setStateValue('careServiceOnOrderCartItems', matchCareGroup);
        });
    }

    // 过滤结账页各个接口的care增值服务, 避免下单，计税、使用优惠等影响（赠品不参与计算）
    if (type === 'filterCare') {
      // 不是美国地区不做处理
      if (!isUSOnConfirmPage || disabledCareGift) return items;

      // 美国地区过滤care
      let itemsAfterFilterCare = [];
      items = JSON.parse(JSON.stringify(items));
      itemsAfterFilterCare = items.map((item) => {
        // 通过bindService type类型字段判断是否为care || care_plus
        item.bind_services = item.bind_services.filter((o) => {
          if (o.type) {
            return o.type?.indexOf('care') < 0;
          }

          if (o.serviceType) {
            return o.serviceType?.indexOf('care') < 0;
          }
        });
        return item;
      });
      return itemsAfterFilterCare;
    }

    // 匹配结账页商品赠品，展示在列表
    if (type === 'handleOrderItemGifts') {
      // 不是美国地区不做处理
      if (!isUSOnConfirmPage || disabledCareGift) return null;
      // Q4大促期间不生效
      if (Q4BFGiftsCareForUS?.enable()) return null;

      const careServiceOnOrderCartItem = careServiceList.find((o) => o.commodityId === listItem.commodityId);
      const listItemServiceGifts = careServiceOnOrderCartItem?.service;
      if (!listItemServiceGifts) return null;

      // 根据OrderSummerItem itemDataHandler 封装数据
      const data = {
        item: {
          ...JSON.parse(JSON.stringify(listItemServiceGifts)),
          count: listItem.count,
          isUSGiftCare: true,
          price: {
            amount: 0,
          },
          origin_price: {
            amount: 0,
          },
        },
        type: 'service',
        country,
      };

      // flexicare隐藏套餐名，避免和产品名重复
      if (careServiceOnOrderCartItem?.serviceType === 'care_plus') {
        data.item.info.name = '';
      }

      // care将套餐名拼接到产品名后，隐藏套餐名
      if (careServiceOnOrderCartItem?.serviceType === 'care') {
        data.item.product_name += ` ${listItemServiceGifts?.info?.name ?? ''}`;
        data.item.info.name = '';
      }

      return data;
    }

    // 封装下单赠品
    if (type === 'handleOrderCreateItems') {
      try {
        if (!isUSOnConfirmPage || disabledCareGift) return null;
        // Q4大促期间不生效，通过活动赠品进行配置
        if (Q4BFGiftsCareForUS?.enable()) return null;

        let otherItems = [];
        items.forEach((item) => {
          const matchCare = this.careServiceOnOrderCartItems.find((o) => o.commodityId === item.commodityId);
          if (matchCare) {
            otherItems.push({
              id: item.commodityId,
              number: item.number,
              bind_services: [
                {
                  id: matchCare.matchCareId,
                  number: item.number,
                  type: matchCare.serviceType,
                },
              ],
            });
          }
        });

        return otherItems;
      } catch (err) {
        feishuRobotSend({
          text: ['US增值服务送增', '封装下单赠品异常', items, this.careServiceOnOrderCartItems],
          at: ['yhq'],
          err,
        });
      }
    }
  };

  /**
   *
   * @description 根据子项、优惠、分销，计算美国商品进口关税
   * @param {Object} data
   * @param {Array} data.items 子项列表
   * @param {String} data.coupon_type 优惠码类型 coupon_type: coupon_code || gift_card_code
   *
   * */
  @action
  getImportDutiesForUS = (data) => {
    return httpRequest({
      url: SERVER.API_SERVER_TRADE + '/order/getCustomsTax',
      method: 'post',
      instaToken: true,
      data,
    });
  };
}
export default Order;
